import {
  CalendarDays,
  CircleCheck,
  ChevronDown,
  ChevronRight,
  Info,
  Plus,
  RefreshCw,
  Save,
  Settings,
  Trash2,
} from "lucide-react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  formatFidelityInputNumber,
  parseFidelityExecutions,
  reconcileFidelityTradeLots,
} from "./fidelityTrade.js";
import {
  canCompleteRebalance,
  rebalanceScheduleStatus,
} from "./portfolioSchedule.js";
import { formatCurrency, formatNumber, formatPercent } from "./replay.js";

const EMPTY_PORTFOLIO = {
  cash: 0,
  holdings: [],
  manual_ticker_states: [],
  name: "Taxable Account",
  performance_start_date: "",
  activation_date: "",
  status: "draft",
  time_zone: "America/Denver",
  notifications: {
    emails: [],
    enabled: false,
  },
  rebalance: {
    annual_tax_budget_fraction: 0.04,
    tax_control: "tax_budget",
    tax_control_trade_limit: 0,
    max_new_position_weight: 0.05,
    max_trades_per_rebalance: 30,
    min_trade_dollars: 1000,
    schedule_anchor_date: "",
    sell_threshold: 0.8,
    strategy: "volume",
  },
  tax_profile: {
    federal_long_term_rate: 0.2,
    federal_short_term_rate: 0.37,
    niit_rate: 0.038,
    state_rate: 0.044,
  },
  viewer_emails: [],
};

const TIME_ZONE_OPTIONS = [
  ["America/Los_Angeles", "Pacific Time"],
  ["America/Denver", "Mountain Time"],
  ["America/Chicago", "Central Time"],
  ["America/New_York", "Eastern Time"],
  ["UTC", "UTC"],
];

const EMPTY_HOLDING = {
  cost_basis_per_share: "",
  purchase_date: "",
  shares: "",
  ticker: "",
};

const EMPTY_TRADE = {
  action: "BUY",
  execution_price: "",
  fees: "",
  funding_source: "portfolio_cash",
  shares: "",
  sold_lots: [],
  ticker: "",
  trade_date: "",
};

const EMPTY_CASH_FLOW = {
  action: "CONTRIBUTION",
  amount: "",
  effective_date: "",
  note: "",
  ticker: "",
};

const EMPTY_TAX_PAYMENT = {
  amount: "",
  effective_date: "",
  jurisdiction: "federal",
  note: "",
  tax_year: "",
};

const PORTFOLIO_TABS = [
  "overview",
  "trade",
  "activity",
  "taxes",
  "performance",
];
const PERFORMANCE_PERIODS = ["daily", "weekly", "monthly", "yearly"];
const PERFORMANCE_REQUEST_DEBOUNCE_MS = 500;
const PERFORMANCE_BENCHMARKS = [
  { key: "nasdaq100", label: "Nasdaq-100" },
  { key: "nasdaq_composite", label: "Nasdaq Composite" },
  { key: "spy", label: "SPY" },
  { key: "inception_hold", label: "Inception Hold" },
];
const PERFORMANCE_CHART_SERIES = [
  { key: "actual", label: "Actual Portfolio" },
  { key: "volume", label: "Volume Strategy" },
  ...PERFORMANCE_BENCHMARKS,
];
const DEFAULT_PERFORMANCE_CHART_SERIES = [
  "actual",
  "volume",
  "nasdaq100",
];

function portfolioUrlStateFromLocation() {
  if (typeof window === "undefined") {
    return defaultPortfolioUrlState();
  }
  const params = new URLSearchParams(window.location.search);
  const tab = params.get("tab") || "";
  const period = params.get("period") || "";
  const startDate = params.get("start") || "";
  return {
    period: PERFORMANCE_PERIODS.includes(period) ? period : "monthly",
    portfolioId: params.get("portfolio") || "",
    startDate: isValidDate(startDate) ? startDate : "",
    tab: PORTFOLIO_TABS.includes(tab) ? tab : "overview",
  };
}

function defaultPortfolioUrlState() {
  return {
    period: "monthly",
    portfolioId: "",
    startDate: "",
    tab: "overview",
  };
}

function writePortfolioUrlState(
  { period, portfolioId, startDate, tab },
  { replace = true } = {},
) {
  if (
    typeof window === "undefined"
    || window.location.pathname !== "/portfolios"
  ) {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  updateOptionalQueryParam(params, "portfolio", portfolioId);
  updateOptionalQueryParam(
    params,
    "tab",
    tab && tab !== "overview" ? tab : "",
  );
  updateOptionalQueryParam(
    params,
    "period",
    period && period !== "monthly" ? period : "",
  );
  updateOptionalQueryParam(params, "start", startDate);
  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextUrl !== currentUrl) {
    const method = replace ? "replaceState" : "pushState";
    window.history[method]({}, "", nextUrl);
  }
}

function updateOptionalQueryParam(params, key, value) {
  const text = String(value || "").trim();
  if (text) {
    params.set(key, text);
  } else {
    params.delete(key);
  }
}

export function PortfoliosPage({ client }) {
  const initialUrlState = portfolioUrlStateFromLocation();
  const [portfolioIndex, setPortfolioIndex] = useState(null);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(
    initialUrlState.portfolioId,
  );
  const [draft, setDraft] = useState(null);
  const [holdingDraft, setHoldingDraft] = useState(EMPTY_HOLDING);
  const [recommendation, setRecommendation] = useState(null);
  const [previewRecommendation, setPreviewRecommendation] = useState(null);
  const [previewRebalanceToday, setPreviewRebalanceToday] = useState(false);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [rebalanceCompleting, setRebalanceCompleting] = useState(false);
  const [recommendationOpen, setRecommendationOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(initialUrlState.tab);
  const [tradeDraft, setTradeDraft] = useState(EMPTY_TRADE);
  const [trading, setTrading] = useState(false);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [taxSummary, setTaxSummary] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxPaymentDraft, setTaxPaymentDraft] = useState(EMPTY_TAX_PAYMENT);
  const [taxPaymentSaving, setTaxPaymentSaving] = useState(false);
  const [performance, setPerformance] = useState(null);
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [performanceSummaryLoading, setPerformanceSummaryLoading] = useState(
    false,
  );
  const [performanceError, setPerformanceError] = useState("");
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performancePeriod, setPerformancePeriod] = useState(
    initialUrlState.period,
  );
  const [performanceStartDate, setPerformanceStartDate] = useState(
    initialUrlState.startDate,
  );
  const [performanceStartDateDraft, setPerformanceStartDateDraft] = useState(
    initialUrlState.startDate,
  );
  const [performanceRefreshKey, setPerformanceRefreshKey] = useState(0);
  const [cashFlowDraft, setCashFlowDraft] = useState(EMPTY_CASH_FLOW);
  const [cashFlowSaving, setCashFlowSaving] = useState(false);
  const [tickerStates, setTickerStates] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFingerprint, setSavedFingerprint] = useState("");
  const [holdingSort, setHoldingSort] = useState({
    direction: "asc",
    key: "ticker",
  });
  const [expandedHoldings, setExpandedHoldings] = useState({});
  const [error, setError] = useState("");
  const performanceRequestRef = useRef(0);
  const performanceSummaryRequestRef = useRef(0);

  const portfolios = useMemo(
    () => portfolioIndex?.portfolios || [],
    [portfolioIndex],
  );
  const holdingTickerKey = useMemo(
    () =>
      [...new Set(
        (draft?.holdings || [])
          .map((holding) => String(holding.ticker || "").trim().toUpperCase())
          .filter(Boolean),
      )]
        .sort()
        .join(","),
    [draft?.holdings],
  );
  const canWrite = Boolean(draft) && draft?.permissions?.can_write !== false;
  const hasUnsavedChanges = Boolean(
    draft
      && (selectedPortfolioId === "new"
        || portfolioFingerprint(draft) !== savedFingerprint),
  );
  const valuedHoldings = useMemo(
    () =>
      buildValuedHoldings(
        draft?.holdings || [],
        recommendation,
        tickerStates,
        draft?.manual_ticker_states || [],
      ),
    [draft, recommendation, tickerStates],
  );
  const sortedHoldings = useMemo(
    () => sortHoldings(valuedHoldings, holdingSort),
    [holdingSort, valuedHoldings],
  );
  const portfolioValuation = useMemo(
    () => buildPortfolioValuation(draft, valuedHoldings, recommendation),
    [draft, recommendation, valuedHoldings],
  );
  const displayedRecommendation = previewRebalanceToday
    ? previewRecommendation
    : recommendation;

  const loadPortfolios = useCallback(async (preferredPortfolioId) => {
    if (!client) {
      return;
    }
    setError("");
    const nextIndex = await client.portfolios();
    setPortfolioIndex(nextIndex);
    const portfolioIds = new Set(
      (nextIndex.portfolios || []).map((portfolio) => portfolio.portfolio_id),
    );
    setSelectedPortfolioId((current) => {
      const candidate =
        preferredPortfolioId === undefined ? current : preferredPortfolioId;
      if (candidate && candidate !== "new" && portfolioIds.has(candidate)) {
        return candidate;
      }
      return nextIndex.portfolios?.[0]?.portfolio_id || "";
    });
    return nextIndex;
  }, [client]);

  const loadPerformanceSummaryData = useCallback(async (portfolioId) => {
    const requestId = performanceSummaryRequestRef.current + 1;
    performanceSummaryRequestRef.current = requestId;
    if (!client || !portfolioId || portfolioId === "new") {
      setPerformanceSummary(null);
      setPerformanceSummaryLoading(false);
      return;
    }
    setPerformanceSummaryLoading(true);
    try {
      const summary = await client.portfolioPerformanceSummary(portfolioId);
      if (performanceSummaryRequestRef.current === requestId) {
        setPerformanceSummary(summary);
      }
    } catch {
      if (performanceSummaryRequestRef.current === requestId) {
        setPerformanceSummary(null);
      }
    } finally {
      if (performanceSummaryRequestRef.current === requestId) {
        setPerformanceSummaryLoading(false);
      }
    }
  }, [client]);

  useEffect(() => {
    loadPortfolios().catch((loadError) => setError(loadError.message));
  }, [loadPortfolios]);

  useEffect(() => {
    const onPopState = () => {
      const nextState = portfolioUrlStateFromLocation();
      setSelectedPortfolioId(nextState.portfolioId);
      setActiveTab(nextState.tab);
      setPerformancePeriod(nextState.period);
      setPerformanceStartDate(nextState.startDate);
      setPerformanceStartDateDraft(nextState.startDate);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    writePortfolioUrlState({
      period: performancePeriod,
      portfolioId: selectedPortfolioId,
      startDate: performanceStartDate,
      tab: activeTab,
    });
  }, [
    activeTab,
    performancePeriod,
    performanceStartDate,
    selectedPortfolioId,
  ]);

  useEffect(() => {
    if (!settingsOpen) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setSettingsOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [settingsOpen]);

  useEffect(() => {
    if (!client || !selectedPortfolioId || selectedPortfolioId === "new") {
      performanceSummaryRequestRef.current += 1;
      setRecommendation(null);
      setPreviewRecommendation(null);
      setPreviewRebalanceToday(false);
      setTickerStates({});
      setTaxSummary(null);
      setPerformanceSummary(null);
      setPerformanceSummaryLoading(false);
      if (!selectedPortfolioId) {
        setDraft(null);
        setSavedFingerprint("");
      }
      return undefined;
    }
    let active = true;
    setPreviewRecommendation(null);
    setPreviewRebalanceToday(false);
    setLoading(true);
    setRecommendationLoading(true);
    setTickerStates({});
    setTaxSummary(null);
    setPerformanceSummary(null);
    setPerformanceSummaryLoading(true);
    setTaxPaymentDraft(EMPTY_TAX_PAYMENT);
    setError("");
    client
      .portfolio(selectedPortfolioId)
      .then((portfolio) => {
        if (active) {
          const normalized = normalizeDraft(portfolio);
          setDraft(normalized);
          setSavedFingerprint(portfolioFingerprint(normalized));
          setPerformanceStartDate(
            (current) => current || normalized.performance_start_date || "",
          );
          setPerformanceStartDateDraft(
            (current) => current || normalized.performance_start_date || "",
          );
          setExpandedHoldings({});
        }
      })
      .catch((loadError) => {
        if (active) {
          setError(loadError.message);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    client
      .portfolioRecommendation(selectedPortfolioId)
      .then((nextRecommendation) => {
        if (active) {
          setRecommendation(nextRecommendation);
        }
      })
      .catch(() => {
        if (active) {
          setRecommendation(null);
        }
      })
      .finally(() => {
        if (active) {
          setRecommendationLoading(false);
        }
      });
    loadPerformanceSummaryData(selectedPortfolioId);
    return () => {
      active = false;
    };
  }, [client, loadPerformanceSummaryData, selectedPortfolioId]);

  useEffect(() => {
    if (!client || !holdingTickerKey) {
      if (!holdingTickerKey) {
        setTickerStates({});
      }
      return undefined;
    }
    const tickers = holdingTickerKey.split(",");
    let active = true;
    setTickerStates((current) =>
      Object.fromEntries(
        tickers.map((ticker) => [
          ticker,
          current[ticker] || { availability: "loading", ticker },
        ]),
      ),
    );
    client
      .tickerStates(tickers)
      .then((payload) => {
        if (active) {
          setTickerStates(payload.states || {});
        }
      })
      .catch(() => {
        if (active) {
          setTickerStates(
            Object.fromEntries(
              tickers.map((ticker) => [
                ticker,
                { availability: "unavailable", ticker },
              ]),
            ),
          );
        }
      });
    return () => {
      active = false;
    };
  }, [
    client,
    holdingTickerKey,
    recommendation?.as_of_date,
    selectedPortfolioId,
  ]);

  const loadActivityData = useCallback(async (portfolioId) => {
    if (!client || !portfolioId || portfolioId === "new") {
      setActivity(null);
      return;
    }
    setActivityLoading(true);
    try {
      setActivity(await client.portfolioActivity(portfolioId));
    } catch {
      setActivity(null);
    } finally {
      setActivityLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (activeTab !== "activity") {
      return;
    }
    loadActivityData(selectedPortfolioId);
  }, [activeTab, loadActivityData, selectedPortfolioId]);

  const loadTaxData = useCallback(async (portfolioId, timeZone) => {
    if (!client || !portfolioId || portfolioId === "new") {
      setTaxSummary(null);
      return;
    }
    setTaxLoading(true);
    try {
      const payload = await client.portfolioTaxes(portfolioId);
      setTaxSummary(payload);
      setTaxPaymentDraft((current) => ({
        ...current,
        effective_date: current.effective_date || todayDate(timeZone),
        tax_year: current.tax_year || String(payload.years?.[0]?.tax_year || ""),
      }));
    } catch (taxError) {
      setTaxSummary(null);
      setError(taxError.message);
    } finally {
      setTaxLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (activeTab !== "taxes") {
      return;
    }
    loadTaxData(selectedPortfolioId, draft?.time_zone);
  }, [
    activeTab,
    draft?.time_zone,
    loadTaxData,
    selectedPortfolioId,
  ]);

  useEffect(() => {
    if (!client || !selectedPortfolioId || selectedPortfolioId === "new") {
      setPerformance(null);
      setPerformanceError("");
      setPerformanceLoading(false);
      return undefined;
    }
    if (activeTab !== "performance") {
      setPerformanceLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const requestId = performanceRequestRef.current + 1;
    performanceRequestRef.current = requestId;
    setPerformanceError("");
    setPerformanceLoading(true);
    const timer = window.setTimeout(() => {
      client
        .portfolioPerformance(
          selectedPortfolioId,
          performancePeriod,
          performanceStartDate,
          { signal: controller.signal },
        )
        .then((nextPerformance) => {
          if (
            controller.signal.aborted
            || performanceRequestRef.current !== requestId
          ) {
            return;
          }
          setPerformance(nextPerformance);
          if (!performanceStartDate && nextPerformance.start_date) {
            setPerformanceStartDate(nextPerformance.start_date);
            setPerformanceStartDateDraft(nextPerformance.start_date);
          }
        })
        .catch((loadError) => {
          if (
            controller.signal.aborted
            || loadError?.name === "AbortError"
            || performanceRequestRef.current !== requestId
          ) {
            return;
          }
          setPerformance(null);
          setPerformanceError(
            loadError.message || "Performance is unavailable.",
          );
        })
        .finally(() => {
          if (
            !controller.signal.aborted
            && performanceRequestRef.current === requestId
          ) {
            setPerformanceLoading(false);
          }
        });
    }, PERFORMANCE_REQUEST_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    activeTab,
    client,
    performancePeriod,
    performanceRefreshKey,
    performanceStartDate,
    selectedPortfolioId,
  ]);

  const setPortfolioTab = useCallback((tab) => {
    setActiveTab(tab);
    writePortfolioUrlState(
      {
        period: performancePeriod,
        portfolioId: selectedPortfolioId,
        startDate: performanceStartDate,
        tab,
      },
      { replace: false },
    );
  }, [
    performancePeriod,
    performanceStartDate,
    selectedPortfolioId,
  ]);

  const createNewPortfolio = () => {
    setSelectedPortfolioId("new");
    setActiveTab("overview");
    writePortfolioUrlState(
      {
        period: performancePeriod,
        portfolioId: "new",
        startDate: "",
        tab: "overview",
      },
      { replace: false },
    );
    setDraft(normalizeDraft(EMPTY_PORTFOLIO));
    setSavedFingerprint("");
    setHoldingDraft(EMPTY_HOLDING);
    setExpandedHoldings({});
    setRecommendation(null);
    setPreviewRecommendation(null);
    setPreviewRebalanceToday(false);
    setRecommendationOpen(false);
    setActivity(null);
    setTaxSummary(null);
    setTaxPaymentDraft(EMPTY_TAX_PAYMENT);
    setPerformance(null);
    setPerformanceSummary(null);
    setPerformanceSummaryLoading(false);
    setPerformanceStartDate("");
    setPerformanceStartDateDraft("");
    setCashFlowDraft(EMPTY_CASH_FLOW);
    setActiveTab("overview");
    setSettingsOpen(true);
    setError("");
  };

  const selectPortfolio = (portfolioId) => {
    setSelectedPortfolioId(portfolioId);
    setTaxSummary(null);
    setTaxPaymentDraft(EMPTY_TAX_PAYMENT);
    setPerformanceStartDate("");
    setPerformanceStartDateDraft("");
    writePortfolioUrlState(
      {
        period: performancePeriod,
        portfolioId,
        startDate: "",
        tab: activeTab,
      },
      { replace: false },
    );
  };

  const refreshRecommendation = async () => {
    if (!client || !selectedPortfolioId || selectedPortfolioId === "new") {
      return;
    }
    setRecommendationLoading(true);
    setError("");
    try {
      if (previewRebalanceToday) {
        setPreviewRecommendation(
          await client.previewPortfolioRecommendation(selectedPortfolioId),
        );
      } else {
        setRecommendation(
          await client.refreshPortfolioRecommendation(selectedPortfolioId),
        );
      }
    } catch (refreshError) {
      if (previewRebalanceToday) {
        setPreviewRecommendation(null);
      } else {
        setRecommendation(null);
      }
      setError(refreshError.message);
    } finally {
      setRecommendationLoading(false);
    }
  };

  const setRebalancePreview = async (enabled) => {
    setPreviewRebalanceToday(enabled);
    setPreviewRecommendation(null);
    if (
      !enabled
      || !client
      || !selectedPortfolioId
      || selectedPortfolioId === "new"
    ) {
      return;
    }
    setRecommendationOpen(true);
    setRecommendationLoading(true);
    setError("");
    try {
      setPreviewRecommendation(
        await client.previewPortfolioRecommendation(selectedPortfolioId),
      );
    } catch (previewError) {
      setPreviewRebalanceToday(false);
      setError(previewError.message);
    } finally {
      setRecommendationLoading(false);
    }
  };

  const completeRebalance = async () => {
    const schedule = recommendation?.rebalance_schedule || {};
    const scheduledDate = schedule.scheduled_rebalance_date || "this cycle";
    if (
      !client
      || !canWrite
      || !selectedPortfolioId
      || selectedPortfolioId === "new"
      || !window.confirm(
        `Complete the ${scheduledDate} rebalance? Remaining recommendations will be recorded as skipped.`,
      )
    ) {
      return;
    }
    setRebalanceCompleting(true);
    setError("");
    try {
      await applyPortfolioMutation(
        await client.completePortfolioRebalance(selectedPortfolioId),
        { refreshRecommendationAfter: false },
      );
    } catch (completionError) {
      setError(completionError.message);
    } finally {
      setRebalanceCompleting(false);
    }
  };

  const savePortfolio = async () => {
    if (!client || !draft || !canWrite || !hasUnsavedChanges) {
      return;
    }
    const validationError = validateDraft(draft, valuedHoldings);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = denormalizeDraft(draft);
      const saved =
        selectedPortfolioId === "new"
          ? await client.createPortfolio(payload)
          : await client.updatePortfolio(selectedPortfolioId, payload);
      const normalized = normalizeDraft(saved);
      setPreviewRecommendation(null);
      setPreviewRebalanceToday(false);
      setDraft(normalized);
      setSavedFingerprint(portfolioFingerprint(normalized));
      setPerformanceSummary(null);
      setPerformanceSummaryLoading(false);
      setPerformanceStartDate(normalized.performance_start_date || "");
      setPerformanceStartDateDraft(normalized.performance_start_date || "");
      setSelectedPortfolioId(saved.portfolio_id);
      await loadPortfolios(saved.portfolio_id);
      await loadPerformanceSummaryData(saved.portfolio_id);
      try {
        setRecommendationLoading(true);
        setRecommendation(
          await client.refreshPortfolioRecommendation(saved.portfolio_id),
        );
      } catch (refreshError) {
        setRecommendation(null);
        setError(
          `Saved, but recommendation refresh failed: ${refreshError.message}`,
        );
      }
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setRecommendationLoading(false);
      setSaving(false);
    }
  };

  const deletePortfolio = async () => {
    if (
      !client
      || !draft
      || !canWrite
      || selectedPortfolioId === "new"
      || deleting
    ) {
      return;
    }
    if (!window.confirm(`Delete portfolio "${draft.name}"?`)) {
      return;
    }
    setDeleting(true);
    setError("");
    try {
      await client.deletePortfolio(selectedPortfolioId);
      setDraft(null);
      setSavedFingerprint("");
      setRecommendation(null);
      setPreviewRecommendation(null);
      setPreviewRebalanceToday(false);
      setActivity(null);
      setTaxSummary(null);
      setPerformance(null);
      setPerformanceSummary(null);
      setPerformanceSummaryLoading(false);
      setSelectedPortfolioId("");
      await loadPortfolios("");
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setDeleting(false);
    }
  };

  const applyPortfolioMutation = async (
    result,
    { refreshRecommendationAfter = true } = {},
  ) => {
    const portfolio = result?.portfolio;
    if (!portfolio) {
      return;
    }
    const normalized = normalizeDraft(portfolio);
    setPreviewRecommendation(null);
    setPreviewRebalanceToday(false);
    setDraft(normalized);
    setSavedFingerprint(portfolioFingerprint(normalized));
    setPerformanceSummary(null);
    setPerformanceSummaryLoading(false);
    setPerformanceStartDate(normalized.performance_start_date || "");
    setPerformanceStartDateDraft(normalized.performance_start_date || "");
    setSelectedPortfolioId(portfolio.portfolio_id);
    await loadPortfolios(portfolio.portfolio_id);
    await loadPerformanceSummaryData(portfolio.portfolio_id);
    if (refreshRecommendationAfter) {
      try {
        setRecommendation(
          await client.refreshPortfolioRecommendation(portfolio.portfolio_id),
        );
      } catch {
        setRecommendation(null);
      }
    } else if (result.recommendation) {
      setRecommendation(result.recommendation);
    }
    await loadActivityData(portfolio.portfolio_id);
    setPerformanceRefreshKey((current) => current + 1);
  };

  const executeManualTrade = async () => {
    if (!client || !canWrite || !selectedPortfolioId || selectedPortfolioId === "new") {
      return;
    }
    const trade = tradeFromDraft(tradeDraft, draft.time_zone);
    if (!trade) {
      setError(
        "Enter valid trade details. Sells must identify the exact tax lots and shares executed.",
      );
      return;
    }
    setTrading(true);
    setError("");
    try {
      await applyPortfolioMutation(
        await client.executeTrade(selectedPortfolioId, trade),
        { refreshRecommendationAfter: false },
      );
      setRecommendation(await client.portfolioRecommendation(selectedPortfolioId));
      setTradeDraft(EMPTY_TRADE);
    } catch (tradeError) {
      setError(tradeError.message);
    } finally {
      setTrading(false);
    }
  };

  const recordCashFlow = async () => {
    if (!client || !canWrite || !selectedPortfolioId || selectedPortfolioId === "new") {
      return;
    }
    const cashFlow = cashFlowFromDraft(cashFlowDraft, draft.time_zone);
    if (!cashFlow) {
      setError("Enter cash flow type, amount, and date.");
      return;
    }
    setCashFlowSaving(true);
    setError("");
    try {
      const mutation =
        cashFlow.action === "DIVIDEND"
          ? client.recordDividend(selectedPortfolioId, cashFlow)
          : client.recordCashFlow(selectedPortfolioId, cashFlow);
      await applyPortfolioMutation(
        await mutation,
        { refreshRecommendationAfter: false },
      );
      setCashFlowDraft(EMPTY_CASH_FLOW);
    } catch (cashFlowError) {
      setError(cashFlowError.message);
    } finally {
      setCashFlowSaving(false);
    }
  };

  const recordTaxPayment = async () => {
    if (
      !client
      || !canWrite
      || !selectedPortfolioId
      || selectedPortfolioId === "new"
    ) {
      return;
    }
    const payment = taxPaymentFromDraft(taxPaymentDraft, draft.time_zone);
    if (!payment) {
      setError("Enter a jurisdiction, tax year, positive amount, and date.");
      return;
    }
    setTaxPaymentSaving(true);
    setError("");
    try {
      await applyPortfolioMutation(
        await client.recordTaxPayment(selectedPortfolioId, payment),
        { refreshRecommendationAfter: false },
      );
      setTaxPaymentDraft((current) => ({
        ...EMPTY_TAX_PAYMENT,
        effective_date: todayDate(draft.time_zone),
        jurisdiction: current.jurisdiction,
        tax_year: current.tax_year,
      }));
      await loadTaxData(selectedPortfolioId, draft.time_zone);
    } catch (taxError) {
      setError(taxError.message);
    } finally {
      setTaxPaymentSaving(false);
    }
  };

  const applyPerformanceStartDate = useCallback(() => {
    if (performanceLoading) {
      return;
    }
    const nextStartDate = performanceStartDateDraft.trim();
    if (nextStartDate && !isValidDate(nextStartDate)) {
      setPerformanceError("Performance start must be a valid date.");
      return;
    }
    setPerformanceError("");
    setPerformanceStartDate(nextStartDate);
    if (nextStartDate === performanceStartDate) {
      setPerformanceRefreshKey((current) => current + 1);
    }
  }, [performanceLoading, performanceStartDate, performanceStartDateDraft]);

  const addOrUpdateHolding = () => {
    if (!canWrite) {
      return;
    }
    const holding = holdingLotFromDraft(holdingDraft);
    if (!holding) {
      setError("Enter a ticker and positive share count.");
      return;
    }
    setDraft((current) => {
      const rows = [...(current?.holdings || [])];
      const existingIndex = rows.findIndex(
        (row) => row.ticker === holding.ticker,
      );
      if (existingIndex >= 0) {
        const existing = rows[existingIndex];
        const lots = [...(existing.lots || []), holding.lot];
        rows[existingIndex] = {
          ...existing,
          lots,
          shares: sumLotShares(lots),
        };
      } else {
        rows.push({
          lots: [holding.lot],
          shares: holding.lot.shares,
          ticker: holding.ticker,
        });
      }
      return {
        ...current,
        holdings: rows.sort((left, right) =>
          left.ticker.localeCompare(right.ticker),
        ),
      };
    });
    setExpandedHoldings((current) => ({
      ...current,
      [holding.ticker]: true,
    }));
    setHoldingDraft(EMPTY_HOLDING);
    setError("");
  };

  const updateHoldingSort = (key) => {
    setHoldingSort((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        key,
        direction: key === "ticker" ? "asc" : "desc",
      };
    });
  };

  const removeHolding = (ticker) => {
    if (!canWrite) {
      return;
    }
    setDraft((current) => ({
      ...current,
      holdings: (current?.holdings || []).filter(
        (holding) => holding.ticker !== ticker,
      ),
    }));
  };

  const toggleHoldingLots = (ticker) => {
    setExpandedHoldings((current) => ({
      ...current,
      [ticker]: !current[ticker],
    }));
  };

  const updateHoldingLots = (ticker, lots) => {
    if (!canWrite) {
      return;
    }
    const normalizedTicker = String(ticker || "").toUpperCase();
    setDraft((current) => {
      const rows = [];
      for (const holding of current?.holdings || []) {
        if (holding.ticker !== normalizedTicker) {
          rows.push(holding);
          continue;
        }
        const nextLots = normalizeEditableLots(lots);
        if (!nextLots.length) {
          continue;
        }
        rows.push({
          ...holding,
          lots: nextLots,
          shares: sumLotShares(nextLots),
        });
      }
      return {
        ...current,
        holdings: rows.sort((left, right) =>
          left.ticker.localeCompare(right.ticker),
        ),
      };
    });
  };

  const updateManualTickerState = (ticker, patch) => {
    if (!canWrite) {
      return;
    }
    const tickerKey = String(ticker || "").toUpperCase();
    setDraft((current) => {
      const rows = [];
      let found = false;
      for (const state of current?.manual_ticker_states || []) {
        if (String(state.ticker || "").toUpperCase() !== tickerKey) {
          rows.push(state);
          continue;
        }
        found = true;
        rows.push({ ...state, ...patch, ticker: tickerKey });
      }
      if (!found) {
        rows.push({
          as_of_date: todayDate(current.time_zone),
          note: "",
          price: "",
          ...patch,
          ticker: tickerKey,
        });
      }
      return {
        ...current,
        manual_ticker_states: rows.sort((left, right) =>
          left.ticker.localeCompare(right.ticker),
        ),
      };
    });
  };

  const startTradeFromHolding = (holding, action) => {
    const normalizedAction = String(action || "BUY").toUpperCase();
    setTradeDraft({
      action: normalizedAction,
      execution_price: holding.price ? String(holding.price) : "",
      fees: "",
      funding_source: "portfolio_cash",
      shares: normalizedAction === "SELL" ? String(holding.shares || "") : "",
      sold_lots:
        normalizedAction === "SELL"
          ? (holding.lots || []).map((lot) => ({
              lot_id: lot.lot_id,
              shares: String(lot.shares),
            }))
          : [],
      ticker: holding.ticker,
      trade_date: todayDate(draft.time_zone),
    });
    setPortfolioTab("trade");
    setError("");
  };

  return (
    <div className="portfolio-page">
      <aside className="portfolio-list-panel">
        <div className="panel-title-row">
          <h2>Portfolios</h2>
          <button
            aria-label="Create portfolio"
            className="icon-button tooltip-button"
            data-tooltip="Create portfolio"
            onClick={createNewPortfolio}
            type="button"
          >
            <Plus size={18} />
          </button>
        </div>
        <div className="portfolio-list">
          {portfolios.map((portfolio) => (
            <button
              aria-current={
                selectedPortfolioId === portfolio.portfolio_id
                  ? "page"
                  : undefined
              }
              className="portfolio-list-item"
              key={`${portfolio.role}-${portfolio.portfolio_id}`}
              onClick={() => selectPortfolio(portfolio.portfolio_id)}
              type="button"
            >
              <strong>{portfolio.name}</strong>
              <span className="portfolio-list-meta">
                <span className="role-badge">
                  {roleLabel(portfolio.role)}
                </span>
                <span>{formatNumber(portfolio.holding_count)} positions</span>
              </span>
            </button>
          ))}
          {!portfolios.length ? (
            <div className="empty-state">No portfolios yet.</div>
          ) : null}
        </div>
      </aside>

      <section className="portfolio-editor-panel">
        {error ? <div className="notice">{error}</div> : null}
        {loading ? <div className="empty-state">Loading portfolio.</div> : null}
        {!draft && !loading ? (
          <div className="empty-state">Select or create a portfolio.</div>
        ) : null}
        {draft ? (
          <>
            <div className="portfolio-editor-header">
              <div className="portfolio-title-block">
                <h2>{draft.name || "New Portfolio"}</h2>
                <p>
                  {canWrite
                    ? "Owner edit mode"
                    : "Read-only shared portfolio"}
                  {" · "}
                  {tabLabel(draft.status || "draft")}
                </p>
              </div>
              <PortfolioValueStrip
                performanceSummary={performanceSummary}
                performanceSummaryLoading={performanceSummaryLoading}
                valuation={portfolioValuation}
              />
              <div className="portfolio-editor-actions">
                <button
                  aria-label="Toggle portfolio settings"
                  aria-expanded={settingsOpen}
                  className="icon-button tooltip-button"
                  data-tooltip="Settings"
                  onClick={() => setSettingsOpen((open) => !open)}
                  type="button"
                >
                  <Settings size={18} />
                </button>
                <button
                  aria-label="Delete portfolio"
                  className="danger-icon-button icon-button tooltip-button"
                  data-tooltip="Delete portfolio"
                  disabled={
                    !canWrite || deleting || selectedPortfolioId === "new"
                  }
                  onClick={deletePortfolio}
                  type="button"
                >
                  <Trash2 size={18} />
                </button>
                <button
                  aria-label="Save portfolio"
                  className="primary-button"
                  disabled={!canWrite || saving || !hasUnsavedChanges}
                  onClick={savePortfolio}
                  type="button"
                >
                  <Save size={18} />
                  {saving ? "Saving" : "Save"}
                </button>
              </div>
            </div>

            {settingsOpen ? (
              <section className="portfolio-settings-popover">
                <div className="portfolio-settings-heading">
                  <div>
                    <h3>Settings</h3>
                    <p>Owner, sharing, cash, notifications, and trade rules</p>
                  </div>
                  <button
                    aria-label="Close portfolio settings"
                    className="icon-button tooltip-button"
                    data-tooltip="Close settings"
                    onClick={() => setSettingsOpen(false)}
                    type="button"
                  >
                    <ChevronDown size={18} />
                  </button>
                </div>
                <div className="portfolio-settings-content">
                  <div className="portfolio-form-grid">
                    <label>
                      <SettingLabel
                        info="Display name for this portfolio."
                        label="Name"
                      />
                      <input
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            name: event.target.value,
                          }))
                        }
                        value={draft.name}
                      />
                    </label>
                    <label>
                      <SettingLabel
                        info="Only the owner can edit holdings, settings, and trade records."
                        label="Owner Email"
                      />
                      <span className="readonly-field">
                        {draft.owner_email || "Assigned on save"}
                      </span>
                    </label>
                    <label>
                      <SettingLabel
                        info="Uninvested cash available to the portfolio. Reserved cash should stay outside this value."
                        label="Cash"
                      />
                      <input
                        disabled={!canWrite}
                        min="0"
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            cash: event.target.value,
                          }))
                        }
                        type="number"
                        value={draft.cash}
                      />
                    </label>
                    <label>
                      <SettingLabel
                        info="Default start date for portfolio performance and what-if comparisons. It can be changed temporarily in the Performance tab."
                        label="Performance Start"
                      />
                      <input
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            performance_start_date: event.target.value,
                          }))
                        }
                        type="date"
                        value={draft.performance_start_date || ""}
                      />
                    </label>
                    <label>
                      <SettingLabel
                        info="Draft portfolios can be edited and preview recommendations without receiving scheduled rebalance emails. Active portfolios use the monthly strategy schedule."
                        label="Portfolio Status"
                      />
                      <select
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            status: event.target.value,
                          }))
                        }
                        value={draft.status || "draft"}
                      >
                        <option value="draft">Draft</option>
                        <option value="active">Active</option>
                      </select>
                    </label>
                    <label>
                      <SettingLabel
                        info="Date when this portfolio starts following scheduled strategy management. Performance defaults to this date unless Performance Start is set separately."
                        label="Activation Date"
                      />
                      <input
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            activation_date: event.target.value,
                          }))
                        }
                        type="date"
                        value={draft.activation_date || ""}
                      />
                    </label>
                    <label>
                      <SettingLabel
                        info="Calendar timezone used for activation, trade-entry defaults, previews, and scheduled portfolio dates."
                        label="Time Zone"
                      />
                      <select
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            time_zone: event.target.value,
                          }))
                        }
                        value={draft.time_zone || "America/Denver"}
                      >
                        {TIME_ZONE_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <SettingLabel
                        info="Emails that may view this portfolio without editing it."
                        label="Viewer Emails"
                      />
                      <input
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            viewer_emails_text: event.target.value,
                          }))
                        }
                        value={draft.viewer_emails_text}
                      />
                    </label>
                    <label>
                      <SettingLabel
                        info="Additional recipients for portfolio emails. The owner receives portfolio emails independently of this list."
                        label="Additional Email Recipients"
                      />
                      <input
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            notification_emails_text: event.target.value,
                          }))
                        }
                        value={draft.notification_emails_text}
                      />
                    </label>
                    <label className="checkbox-row">
                      <input
                        checked={Boolean(draft.notifications?.enabled)}
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            notifications: {
                              ...(current.notifications || {}),
                              enabled: event.target.checked,
                            },
                          }))
                        }
                        type="checkbox"
                      />
                      <span className="checkbox-label-copy">
                        <SettingLabel
                          info="Send portfolio emails to additional recipients listed above."
                          label="Additional recipient emails"
                        />
                      </span>
                    </label>
                  </div>
                  <div className="portfolio-settings-grid">
                    <label>
                      <SettingLabel
                        info="Recommendation engine used for rebalance actions. Spike-damped volume is the current production strategy."
                        label="Recommendation Strategy"
                      />
                      <select
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            rebalance: {
                              ...(current.rebalance || {}),
                              strategy: event.target.value,
                            },
                          }))
                        }
                        value={
                          draft.rebalance?.strategy || "volume"
                        }
                      >
                        <option value="volume">Volume Strategy</option>
                      </select>
                    </label>
                    <label>
                      <SettingLabel
                        info="Controls how strictly recommendations limit taxable sales while applying the strategy."
                        label="Tax Control"
                      />
                      <select
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            rebalance: {
                              ...(current.rebalance || {}),
                              tax_control: event.target.value,
                            },
                          }))
                        }
                        value={
                          draft.rebalance?.tax_control || "tax_budget"
                        }
                      >
                        <option value="tax_budget">Tax budget</option>
                        <option value="convergence">Convergence</option>
                      </select>
                    </label>
                    <NumberSetting
                      disabled={!canWrite}
                      draft={draft}
                      info="Extra tax-controlled sells allowed at each rebalance. Zero means use the annual tax budget only."
                      label="Tax-Controlled Trades"
                      name="tax_control_trade_limit"
                      setDraft={setDraft}
                    />
                    <label>
                      <SettingLabel
                        info="Fixed calendar anchor for monthly rebalances. Market holidays move an individual rebalance without changing future months."
                        label="Rebalance Anchor"
                      />
                      <input
                        disabled={!canWrite}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            rebalance: {
                              ...(current.rebalance || {}),
                              schedule_anchor_date: event.target.value,
                            },
                          }))
                        }
                        type="date"
                        value={draft.rebalance?.schedule_anchor_date || ""}
                      />
                    </label>
                    <NumberSetting
                      disabled={!canWrite}
                      draft={draft}
                      info="Maximum realized-gain tax the recommendation may use each year as a fraction of portfolio value."
                      label="Annual Tax Budget"
                      name="annual_tax_budget_fraction"
                      setDraft={setDraft}
                    />
                    <NumberSetting
                      disabled={!canWrite}
                      draft={draft}
                      info="Maximum intermediate trades per rebalance. Initial setup and liquidation are not part of this control."
                      label="Max Trades"
                      name="max_trades_per_rebalance"
                      setDraft={setDraft}
                    />
                    <NumberSetting
                      disabled={!canWrite}
                      draft={draft}
                      info="Maximum weight for a newly opened position. Existing winners can grow above this weight."
                      label="Max New Weight"
                      name="max_new_position_weight"
                      setDraft={setDraft}
                    />
                    <NumberSetting
                      disabled={!canWrite}
                      draft={draft}
                      info="Small proposed trades below this dollar amount are skipped."
                      label="Min Trade"
                      name="min_trade_dollars"
                      setDraft={setDraft}
                    />
                  </div>
                </div>
              </section>
            ) : null}

            <div className="portfolio-main-scroll">
              <PortfolioTabBar
                activeTab={activeTab}
                onChange={setPortfolioTab}
              />

              <div className="portfolio-tab-content" key={activeTab}>
                {activeTab === "overview" ? (
                  <>
                    <RecommendationPanel
                      canComplete={canWrite}
                      canPreview={selectedPortfolioId !== "new"}
                      completeRebalance={completeRebalance}
                      completing={rebalanceCompleting}
                      currentDate={todayDate(draft.time_zone)}
                      loading={recommendationLoading}
                      onPreviewChange={setRebalancePreview}
                      onToggle={() => setRecommendationOpen((open) => !open)}
                      open={recommendationOpen}
                      previewEnabled={previewRebalanceToday}
                      recommendation={displayedRecommendation}
                      refreshRecommendation={refreshRecommendation}
                    />
                    <HoldingsEditor
                      canWrite={canWrite}
                      expandedHoldings={expandedHoldings}
                      holdingDraft={holdingDraft}
                      holdingSort={holdingSort}
                      onAddHolding={addOrUpdateHolding}
                      onHoldingDraftChange={setHoldingDraft}
                      onHoldingLotsChange={updateHoldingLots}
                      onManualTickerStateChange={updateManualTickerState}
                      onStartTrade={startTradeFromHolding}
                      onRemoveHolding={removeHolding}
                      onSort={updateHoldingSort}
                      onToggleHoldingLots={toggleHoldingLots}
                      sortedHoldings={sortedHoldings}
                      timeZone={draft.time_zone}
                      totalHoldings={draft.holdings.length}
                    />
                  </>
                ) : null}
                {activeTab === "trade" ? (
                  <TradePanel
                    canWrite={canWrite}
                    executeManualTrade={executeManualTrade}
                    holdings={valuedHoldings}
                    recommendation={recommendation}
                    setTradeDraft={setTradeDraft}
                    timeZone={draft.time_zone}
                    tradeDraft={tradeDraft}
                    trading={trading}
                  />
                ) : null}
                {activeTab === "activity" ? (
                  <ActivityPanel
                    activity={activity}
                    loading={activityLoading}
                    timeZone={draft.time_zone}
                  />
                ) : null}
                {activeTab === "taxes" ? (
                  <TaxPanel
                    canWrite={canWrite}
                    loading={taxLoading}
                    onPaymentChange={setTaxPaymentDraft}
                    onPaymentSubmit={recordTaxPayment}
                    paymentDraft={taxPaymentDraft}
                    paymentSaving={taxPaymentSaving}
                    summary={taxSummary}
                  />
                ) : null}
                {activeTab === "performance" ? (
                  <PerformancePanel
                    canWrite={canWrite}
                    cashFlowDraft={cashFlowDraft}
                    cashFlowSaving={cashFlowSaving}
                    error={performanceError}
                    loading={performanceLoading}
                    onApplyStartDate={applyPerformanceStartDate}
                    onCashFlowChange={setCashFlowDraft}
                    onCashFlowSubmit={recordCashFlow}
                    onPeriodChange={setPerformancePeriod}
                    onStartDateChange={setPerformanceStartDateDraft}
                    performance={performance}
                    period={performancePeriod}
                    startDate={performanceStartDate}
                    startDateDraft={performanceStartDateDraft}
                  />
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}

function PortfolioValueStrip({
  performanceSummary,
  performanceSummaryLoading,
  valuation,
}) {
  const sinceStartGain = numberOrNull(performanceSummary?.gain_loss);
  const sinceStartPercent = numberOrNull(
    performanceSummary?.gain_loss_percent,
  );
  const hasSinceStartPerformance = ["hit", "live"].includes(
    performanceSummary?.cache_status,
  );
  const sinceStartValue = performanceSummaryLoading
    ? "Loading…"
    : hasSinceStartPerformance
      ? formatOptionalCurrency(sinceStartGain)
      : "—";
  const sinceStartDetail = hasSinceStartPerformance
    ? `${formatOptionalPercent(sinceStartPercent)} · after tax`
    : "After tax";
  return (
    <div className="portfolio-valuation-panel">
      <div className="portfolio-value-strip">
        <Metric
          label="Total Value"
          value={formatOptionalCurrency(valuation.portfolioValue)}
        />
        <Metric
          label="Overall Gain/Loss"
          detail={formatOptionalPercent(valuation.unrealizedGainPercent)}
          tone={gainTone(valuation.unrealizedGain)}
          value={formatOptionalCurrency(valuation.unrealizedGain)}
        />
        <Metric
          detail={sinceStartDetail}
          info="After-tax gain/loss since the configured performance start. Securities without historical market data are excluded."
          label="Since Start"
          tone={gainTone(sinceStartGain)}
          value={sinceStartValue}
        />
        <Metric label="Cash" value={formatCurrency(valuation.cash)} />
      </div>
      <div className="target-freshness-row">
        <p>
          {valuation.asOfDate
            ? `Latest prices as of ${valuation.asOfDate}`
            : "Waiting for latest recommendation prices"}
        </p>
        {valuation.targetStatus ? (
          <span className={`freshness-pill ${valuation.targetStatus}`}>
            {targetStatusLabel(valuation)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function PortfolioTabBar({ activeTab, onChange }) {
  return (
    <div className="portfolio-tabs" role="tablist">
      {PORTFOLIO_TABS.map((tab) => (
        <button
          aria-selected={activeTab === tab}
          className="portfolio-tab"
          key={tab}
          onClick={() => onChange(tab)}
          role="tab"
          type="button"
        >
          {tabLabel(tab)}
        </button>
      ))}
    </div>
  );
}

function HoldingsEditor({
  canWrite,
  expandedHoldings,
  holdingDraft,
  holdingSort,
  onAddHolding,
  onHoldingDraftChange,
  onHoldingLotsChange,
  onManualTickerStateChange,
  onRemoveHolding,
  onSort,
  onStartTrade,
  onToggleHoldingLots,
  sortedHoldings,
  timeZone,
  totalHoldings,
}) {
  return (
    <section className="holdings-editor">
      <div className="panel-title-row">
        <h2>Holdings</h2>
        <p>{formatNumber(totalHoldings)} positions</p>
      </div>
      <div className="holding-entry-grid">
        <label>
          Ticker
          <input
            disabled={!canWrite}
            onChange={(event) =>
              onHoldingDraftChange((current) => ({
                ...current,
                ticker: event.target.value.toUpperCase(),
              }))
            }
            value={holdingDraft.ticker}
          />
        </label>
        <label>
          Shares
          <input
            disabled={!canWrite}
            min="0"
            onChange={(event) =>
              onHoldingDraftChange((current) => ({
                ...current,
                shares: event.target.value,
              }))
            }
            type="number"
            value={holdingDraft.shares}
          />
        </label>
        <label>
          Cost/Share
          <input
            disabled={!canWrite}
            min="0"
            onChange={(event) =>
              onHoldingDraftChange((current) => ({
                ...current,
                cost_basis_per_share: event.target.value,
              }))
            }
            type="number"
            value={holdingDraft.cost_basis_per_share}
          />
        </label>
        <label>
          Purchase Date
          <input
            disabled={!canWrite}
            onChange={(event) =>
              onHoldingDraftChange((current) => ({
                ...current,
                purchase_date: event.target.value,
              }))
            }
            type="date"
            value={holdingDraft.purchase_date}
          />
        </label>
        <button
          disabled={!canWrite}
          onClick={onAddHolding}
          type="button"
        >
          <Plus size={18} />
          Add lot
        </button>
      </div>
      <div className="portfolio-table-wrap">
        <table>
          <thead>
            <tr>
              <SortableHeader
                currentSort={holdingSort}
                label="Ticker"
                onSort={onSort}
                sortKey="ticker"
              />
              <th>Shares</th>
              <th>Price</th>
              <SortableHeader
                currentSort={holdingSort}
                label="Value"
                onSort={onSort}
                sortKey="market_value"
              />
              <th>Cost Basis</th>
              <SortableHeader
                currentSort={holdingSort}
                label="Gain"
                onSort={onSort}
                sortKey="unrealized_gain"
              />
              <SortableHeader
                currentSort={holdingSort}
                label="Gain %"
                onSort={onSort}
                sortKey="unrealized_gain_percent"
              />
              <th>Tax Lots</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedHoldings.map((holding) => (
              <Fragment key={holding.ticker}>
                <tr>
                  <td>
                    <TickerCell holding={holding} timeZone={timeZone} />
                  </td>
                  <td>{formatNumber(holding.shares)}</td>
                  <td>{formatOptionalCurrency(holding.price)}</td>
                  <td>{formatOptionalCurrency(holding.market_value)}</td>
                  <td>{formatOptionalCurrency(holding.cost_basis)}</td>
                  <td className={gainClassName(holding.unrealized_gain)}>
                    {formatOptionalCurrency(holding.unrealized_gain)}
                  </td>
                  <td className={gainClassName(holding.unrealized_gain)}>
                    {formatOptionalPercent(holding.unrealized_gain_percent)}
                  </td>
                  <td>
                    <button
                      aria-expanded={Boolean(expandedHoldings[holding.ticker])}
                      aria-label={`Edit ${holding.ticker} tax lots`}
                      className="lot-toggle-button"
                      onClick={() => onToggleHoldingLots(holding.ticker)}
                      type="button"
                    >
                      {expandedHoldings[holding.ticker] ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                      <span>{formatNumber(holding.lots.length)} lots</span>
                    </button>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="compact-action-button"
                        disabled={!canWrite}
                        onClick={() => onStartTrade(holding, "BUY")}
                        type="button"
                      >
                        Buy
                      </button>
                      <button
                        className="compact-action-button"
                        disabled={!canWrite || Number(holding.shares || 0) <= 0}
                        onClick={() => onStartTrade(holding, "SELL")}
                        type="button"
                      >
                        Sell
                      </button>
                      <button
                        aria-label={`Remove ${holding.ticker}`}
                        className="icon-button tooltip-button"
                        data-tooltip="Remove"
                        disabled={!canWrite}
                        onClick={() => onRemoveHolding(holding.ticker)}
                        type="button"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedHoldings[holding.ticker] ? (
                  <tr className="lot-editor-row">
                    <td colSpan="9">
                      <TaxLotEditor
                        canWrite={canWrite}
                        holding={holding}
                        onChange={onHoldingLotsChange}
                        onManualTickerStateChange={onManualTickerStateChange}
                        timeZone={timeZone}
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TickerCell({ holding, timeZone }) {
  const note = holdingStatusNote(holding, timeZone);
  return (
    <span className="ticker-cell">
      <span>{holding.ticker}</span>
      {note ? (
        <span
          aria-label={note}
          data-tooltip={note}
          className={`ticker-note ${holding.strategy_status || ""}`}
          role="img"
          tabIndex={0}
        />
      ) : null}
    </span>
  );
}

function TaxLotEditor({
  canWrite,
  holding,
  onChange,
  onManualTickerStateChange,
  timeZone,
}) {
  const lots = holding.lots || [];
  const [pasteError, setPasteError] = useState("");
  const [pasteMessage, setPasteMessage] = useState("");
  const [pasteText, setPasteText] = useState("");
  const pasteInputId = `lot-paste-${holding.ticker.replace(/[^A-Z0-9_-]/g, "-")}`;
  const updateLot = (lotId, patch) => {
    onChange(
      holding.ticker,
      lots.map((lot) =>
        lot.lot_id === lotId ? { ...lot, ...patch } : lot,
      ),
    );
  };
  const removeLot = (lotId) => {
    onChange(
      holding.ticker,
      lots.filter((lot) => lot.lot_id !== lotId),
    );
  };
  const addLot = () => {
    onChange(holding.ticker, [...lots, newEditableLot()]);
  };
  const importPastedLots = (mode) => {
    const result = parseFidelityLots(pasteText);
    if (result.error) {
      setPasteError(result.error);
      setPasteMessage("");
      return;
    }
    const nextLots =
      mode === "append" ? [...lots, ...result.lots] : result.lots;
    onChange(holding.ticker, nextLots);
    setPasteError("");
    setPasteMessage(
      `${mode === "append" ? "Appended" : "Replaced with"} ${formatNumber(
        result.lots.length,
      )} lots, ${formatNumber(sumLotShares(result.lots))} shares.`,
    );
    setPasteText("");
  };
  return (
    <div className="lot-editor-panel">
      <div className="panel-title-row">
        <h3>{holding.ticker} Tax Lots</h3>
        <button
          className="compact-action-button"
          disabled={!canWrite}
          onClick={addLot}
          type="button"
        >
          <Plus size={16} />
          Add lot
        </button>
      </div>
      <div className="lot-paste-panel">
        <label htmlFor={pasteInputId}>Paste Fidelity lots</label>
        <textarea
          aria-label="Paste Fidelity tax lots"
          disabled={!canWrite}
          id={pasteInputId}
          onChange={(event) => {
            setPasteText(event.target.value);
            setPasteError("");
            setPasteMessage("");
          }}
          placeholder={
            "Acquired\tTerm\t...\tQuantity\tAverage cost basis\tCost basis total"
          }
          rows="4"
          value={pasteText}
        />
        <div className="lot-paste-actions">
          {pasteError ? (
            <span className="lot-paste-error" role="alert">
              {pasteError}
            </span>
          ) : null}
          {pasteMessage ? (
            <span className="lot-paste-message">{pasteMessage}</span>
          ) : null}
          <button
            className="compact-action-button"
            disabled={!canWrite || !pasteText.trim()}
            onClick={() => importPastedLots("append")}
            type="button"
          >
            Append lots
          </button>
          <button
            className="compact-action-button"
            disabled={!canWrite || !pasteText.trim()}
            onClick={() => importPastedLots("replace")}
            type="button"
          >
            Replace lots
          </button>
        </div>
      </div>
      {["manual_price", "unsupported"].includes(holding.strategy_status) ? (
        <div className="manual-price-grid">
          <label>
            Current Price
            <input
              disabled={!canWrite}
              min="0"
              onChange={(event) =>
                onManualTickerStateChange(holding.ticker, {
                  price: event.target.value,
                })
              }
              step="any"
              type="number"
              value={holding.manual_price ?? ""}
            />
          </label>
          <label>
            Price Date
            <input
              disabled={!canWrite}
              onChange={(event) =>
                onManualTickerStateChange(holding.ticker, {
                  as_of_date: event.target.value,
                })
              }
              type="date"
              value={holding.manual_price_as_of_date || todayDate(timeZone)}
            />
          </label>
          <label>
            Note
            <input
              disabled={!canWrite}
              onChange={(event) =>
                onManualTickerStateChange(holding.ticker, {
                  note: event.target.value,
                })
              }
              value={holding.manual_price_note || ""}
            />
          </label>
        </div>
      ) : null}
      <div className="lot-editor-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Purchase Date</th>
              <th>Shares</th>
              <th>Cost/Share</th>
              <th>Lot Cost</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {lots.map((lot) => (
              <tr key={lot.lot_id}>
                <td>
                  <input
                    disabled={!canWrite}
                    onChange={(event) =>
                      updateLot(lot.lot_id, {
                        purchase_date: event.target.value,
                      })
                    }
                    type="date"
                    value={lot.purchase_date || ""}
                  />
                </td>
                <td>
                  <input
                    disabled={!canWrite}
                    min="0"
                    onChange={(event) =>
                      updateLot(lot.lot_id, { shares: event.target.value })
                    }
                    step="any"
                    type="number"
                    value={lot.shares ?? ""}
                  />
                </td>
                <td>
                  <input
                    disabled={!canWrite}
                    min="0"
                    onChange={(event) =>
                      updateLot(lot.lot_id, {
                        cost_basis_per_share: event.target.value,
                      })
                    }
                    step="any"
                    type="number"
                    value={lot.cost_basis_per_share ?? ""}
                  />
                </td>
                <td>
                  {formatOptionalCurrency(
                    lotCost(lot.shares, lot.cost_basis_per_share),
                  )}
                </td>
                <td>
                  <button
                    aria-label={`Delete ${holding.ticker} lot`}
                    className="icon-button tooltip-button"
                    data-tooltip="Delete lot"
                    disabled={!canWrite}
                    onClick={() => removeLot(lot.lot_id)}
                    type="button"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
            {!lots.length ? (
              <tr>
                <td colSpan="5">No tax lots.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecommendationPanel({
  canComplete,
  canPreview,
  completeRebalance,
  completing,
  currentDate,
  loading,
  onPreviewChange,
  onToggle,
  open,
  previewEnabled,
  recommendation,
  refreshRecommendation,
}) {
  const actions = recommendation?.actions || [];
  const currentSchedule = recommendation?.current_rebalance_schedule;
  const completedCycle = recommendation?.workflow?.status === "complete";
  const recommendationTitle = previewEnabled
    ? "Today's Rebalance Preview"
    : completedCycle
    ? "Last Completed Rebalance"
    : "Latest Recommendation";
  const rebalanceStatus = rebalanceScheduleStatus(
    currentSchedule || recommendation?.rebalance_schedule,
    currentDate,
    currentSchedule ? "" : recommendation?.workflow?.status,
  );
  const canCompleteCurrentCycle =
    canComplete
    && !previewEnabled
    && canCompleteRebalance(recommendation, currentDate);
  return (
    <section className="recommendation-panel">
      <div className="panel-title-row">
        <button
          aria-label="Toggle recommendation details"
          aria-expanded={open}
          className="collapsible-title-button"
          onClick={onToggle}
          type="button"
        >
          {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <span className="collapsible-title-copy">
            <span className="recommendation-heading-line">
              <strong>{recommendationTitle}</strong>
              {rebalanceStatus ? (
                <span
                  className={`rebalance-date-status ${rebalanceStatus.tone}`}
                  data-testid="next-rebalance-date"
                >
                  <CalendarDays aria-hidden="true" size={14} />
                  <span>{rebalanceStatus.label}</span>
                  <strong>{rebalanceStatus.date}</strong>
                </span>
              ) : null}
            </span>
            <small>
              {loading && previewEnabled && !recommendation
                ? "Calculating a hypothetical rebalance from the latest market data."
                : recommendation
                ? recommendationSubtitle(recommendation)
                : "No recommendation published yet for this portfolio."}
            </small>
          </span>
        </button>
        <div className="recommendation-title-actions">
          <label className="recommendation-preview-toggle">
            <input
              aria-label="Preview rebalance today"
              checked={previewEnabled}
              disabled={!canPreview || loading}
              onChange={(event) => onPreviewChange(event.target.checked)}
              type="checkbox"
            />
            <span>Preview rebalance today</span>
          </label>
          <button
            aria-label={
              previewEnabled ? "Refresh rebalance preview" : "Refresh recommendation"
            }
            className="icon-button tooltip-button"
            data-tooltip={
              previewEnabled ? "Refresh rebalance preview" : "Refresh recommendation"
            }
            disabled={loading}
            onClick={refreshRecommendation}
            type="button"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>
      {loading ? <LoadingProgress label="Loading recommendation" /> : null}
      {open && recommendation ? (
        <>
          {canCompleteCurrentCycle ? (
            <div className="rebalance-completion-row">
              <span>
                Finished trading? Complete this cycle to advance the schedule.
                {recommendationOutstandingActionCount(recommendation) > 0
                  ? " Remaining recommendations will be recorded as skipped."
                  : ""}
              </span>
              <button
                disabled={completing}
                onClick={completeRebalance}
                type="button"
              >
                <CircleCheck aria-hidden="true" size={16} />
                {completing ? "Completing" : "Complete rebalance"}
              </button>
            </div>
          ) : null}
          <div className="recommendation-summary-grid">
            <Metric
              label="Portfolio"
              value={formatCurrency(recommendation.summary?.portfolio_value || 0)}
            />
            <Metric
              label="Est. Tax"
              value={formatCurrency(recommendation.summary?.estimated_tax || 0)}
            />
            <Metric
              label="Est. Costs"
              value={formatCurrency(
                recommendation.summary?.estimated_transaction_cost || 0,
              )}
            />
            <Metric
              label="Trades"
              value={formatNumber(recommendation.summary?.trade_count || 0)}
            />
            <Metric
              detail={exposurePolicyLabel(
                recommendation.summary?.exposure_policy_reason,
              )}
              label="Stock Exposure"
              value={formatPercent(
                recommendation.summary?.target_gross_exposure ?? 1,
              )}
            />
          </div>
          <div className="portfolio-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Ticker</th>
                  <th>Shares</th>
                  <th>Value</th>
                  <th>Tax</th>
                  <th>Status</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {actions.map((action, index) => (
                  <tr key={`${action.action}-${action.ticker}-${index}`}>
                    <td>{action.action}</td>
                    <td>{action.ticker}</td>
                    <td>{formatNumber(action.shares)}</td>
                    <td>{formatCurrency(action.estimated_trade_value || 0)}</td>
                    <td>{formatCurrency(action.estimated_tax || 0)}</td>
                    <td>
                      <span
                        className={`status-pill ${actionStatus(
                          action,
                          previewEnabled,
                        )}`}
                      >
                        {statusLabel(action, previewEnabled)}
                      </span>
                    </td>
                    <td>{action.reason}</td>
                  </tr>
                ))}
                {!actions.length ? (
                  <tr>
                    <td colSpan="7">
                      {recommendation.warnings?.length
                        ? "No executable trades."
                        : "No trades recommended."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {recommendation.warnings?.length ? (
            <div className="notice">
              {recommendation.warnings.slice(0, 3).join(" ")}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function FidelityTradeImport({
  action,
  canWrite,
  currentLots,
  setTradeDraft,
  ticker,
}) {
  const [error, setError] = useState("");
  const [executionsText, setExecutionsText] = useState("");
  const [message, setMessage] = useState("");
  const [resultingLotsText, setResultingLotsText] = useState("");
  const normalizedAction = String(action || "").toUpperCase();
  const normalizedTicker = String(ticker || "").trim().toUpperCase();
  const clearStatus = () => {
    setError("");
    setMessage("");
  };
  const prepareTrade = () => {
    clearStatus();
    if (!normalizedTicker) {
      setError("Select the traded ticker first.");
      return;
    }
    const parsedExecutions = parseFidelityExecutions(
      executionsText,
      normalizedAction,
    );
    if (parsedExecutions.error) {
      setError(parsedExecutions.error);
      return;
    }
    const noRemainingLots =
      resultingLotsText.trim().toUpperCase() === "NONE";
    if (normalizedAction === "BUY" && noRemainingLots) {
      setError("A buy must appear in the resulting Fidelity holdings.");
      return;
    }
    const parsedLots = noRemainingLots
      ? { lots: [] }
      : parseFidelityLots(resultingLotsText);
    if (parsedLots.error) {
      setError(parsedLots.error);
      return;
    }
    const reconciliation = reconcileFidelityTradeLots({
      action: normalizedAction,
      currentLots,
      executedShares: parsedExecutions.execution.shares,
      resultingLots: parsedLots.lots,
    });
    if (reconciliation.error) {
      setError(reconciliation.error);
      return;
    }
    const execution = parsedExecutions.execution;
    setTradeDraft((current) => ({
      ...current,
      action: normalizedAction,
      execution_price: formatFidelityInputNumber(execution.execution_price),
      fees: formatFidelityInputNumber(execution.fees, 2),
      shares: formatFidelityInputNumber(execution.shares, 8),
      sold_lots: reconciliation.sold_lots,
      ticker: normalizedTicker,
      trade_date: execution.trade_date,
    }));
    const shareLabel = Math.abs(execution.shares - 1) < 1e-9
      ? "share"
      : "shares";
    const fillLabel = execution.execution_count === 1 ? "fill" : "fills";
    setMessage(
      `Prepared ${formatNumber(execution.shares)} ${shareLabel} from ${formatNumber(
        execution.execution_count,
      )} ${fillLabel} at ${formatExecutionCurrency(
        execution.execution_price,
        4,
      )} average, ${formatExecutionCurrency(
        execution.fees,
      )} fees, ${formatExecutionCurrency(execution.net_amount)} net.`,
    );
  };
  return (
    <details className="fidelity-trade-import">
      <summary>Import Fidelity trade</summary>
      <p>
        Paste the executions and the ticker&apos;s holdings by lot after the
        trade. This prepares the form for review; it does not execute it.
      </p>
      <div className="fidelity-trade-import-grid">
        <label>
          Fidelity executions
          <textarea
            aria-label="Paste Fidelity trade executions"
            disabled={!canWrite}
            onChange={(event) => {
              setExecutionsText(event.target.value);
              clearStatus();
            }}
            placeholder={
              "Executions for this trade\nDate\tTime\tPrice\tQuantity\tAmount\n...\nNet total\t...\t..."
            }
            rows="7"
            value={executionsText}
          />
        </label>
        <label>
          Fidelity holdings after trade
          <textarea
            aria-label="Paste Fidelity holdings after trade"
            disabled={!canWrite}
            onChange={(event) => {
              setResultingLotsText(event.target.value);
              clearStatus();
            }}
            placeholder={
              normalizedAction === "SELL"
                ? "Acquired\tTerm\t...\tQuantity\tAverage cost basis\tCost basis total\nEnter NONE if the position was fully sold."
                : "Acquired\tTerm\t...\tQuantity\tAverage cost basis\tCost basis total"
            }
            rows="7"
            value={resultingLotsText}
          />
        </label>
      </div>
      <div className="fidelity-trade-import-actions">
        {error ? (
          <span className="lot-paste-error" role="alert">
            {error}
          </span>
        ) : null}
        {message ? (
          <span className="lot-paste-message">{message}</span>
        ) : null}
        <button
          className="compact-action-button"
          disabled={
            !canWrite
            || !normalizedTicker
            || !executionsText.trim()
            || !resultingLotsText.trim()
          }
          onClick={prepareTrade}
          type="button"
        >
          Prepare {normalizedAction === "SELL" ? "sale" : "purchase"}
        </button>
      </div>
    </details>
  );
}

function formatExecutionCurrency(value, maximumFractionDigits = 2) {
  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits,
    minimumFractionDigits: 2,
    style: "currency",
  }).format(value);
}

function TradePanel({
  canWrite,
  executeManualTrade,
  holdings,
  recommendation,
  setTradeDraft,
  timeZone,
  tradeDraft,
  trading,
}) {
  const actions = recommendation?.actions || [];
  const selectedHolding = (holdings || []).find(
    (holding) => holding.ticker === String(tradeDraft.ticker || "").toUpperCase(),
  );
  const availableLots = selectedHolding?.lots || [];
  const soldLotsById = new Map(
    (tradeDraft.sold_lots || []).map((lot) => [lot.lot_id, lot.shares]),
  );
  const setSoldLotShares = (lotId, value) => {
    setTradeDraft((current) => {
      const rows = (current.sold_lots || []).filter((lot) => lot.lot_id !== lotId);
      if (String(value).trim()) {
        rows.push({ lot_id: lotId, shares: value });
      }
      const shares = rows.reduce(
        (total, lot) => total + (Number(lot.shares) > 0 ? Number(lot.shares) : 0),
        0,
      );
      return { ...current, shares: shares ? String(shares) : "", sold_lots: rows };
    });
  };
  const startRecommendedTrade = (action) => {
    const ticker = String(action.ticker || "").toUpperCase();
    const holding = (holdings || []).find((row) => row.ticker === ticker);
    const soldLots = String(action.action || "").toUpperCase() === "SELL"
      ? recommendedSoldLots(action.lots_to_sell, holding?.lots || [])
      : [];
    setTradeDraft({
      action: String(action.action || "").toUpperCase(),
      execution_price: "",
      fees: "",
      funding_source: "portfolio_cash",
      shares: soldLots.length
        ? String(sumSoldLotShares(soldLots))
        : action.shares
          ? String(action.shares)
          : "",
      sold_lots: soldLots,
      ticker,
      trade_date: todayDate(timeZone),
    });
  };
  return (
    <section className="portfolio-detail-panel trade-panel">
      <div className="trade-control-panel">
        <div className="trade-entry-grid">
          <label>
            Action
            <select
              disabled={!canWrite}
              onChange={(event) =>
                setTradeDraft((current) => ({
                  ...current,
                  action: event.target.value,
                  shares: "",
                  sold_lots: [],
                }))
              }
              value={tradeDraft.action}
            >
              <option value="BUY">Buy</option>
              <option value="SELL">Sell</option>
            </select>
          </label>
          <label>
            Ticker
            <input
              disabled={!canWrite}
              onChange={(event) =>
                setTradeDraft((current) => ({
                  ...current,
                  ticker: event.target.value.toUpperCase(),
                  shares: current.action === "SELL" ? "" : current.shares,
                  sold_lots: [],
                }))
              }
              value={tradeDraft.ticker}
            />
          </label>
          <label>
            Shares
            <input
              disabled={!canWrite || tradeDraft.action === "SELL"}
              min="0"
              onChange={(event) =>
                setTradeDraft((current) => ({
                  ...current,
                  shares: event.target.value,
                }))
              }
              type="number"
              value={tradeDraft.shares}
            />
          </label>
          {tradeDraft.action === "SELL" ? (
            <div className="trade-lot-selector">
              <span className="trade-lot-selector-title">Executed Tax Lots</span>
              {availableLots.length ? (
                <div className="trade-lot-selector-rows">
                  {availableLots.map((lot) => (
                    <label key={lot.lot_id}>
                      <span>
                        {lot.purchase_date || "Unknown date"} · {formatNumber(lot.shares)} available
                      </span>
                      <input
                        aria-label={`${tradeDraft.ticker} ${lot.purchase_date || lot.lot_id} shares sold`}
                        disabled={!canWrite}
                        max={lot.shares}
                        min="0"
                        onChange={(event) =>
                          setSoldLotShares(lot.lot_id, event.target.value)
                        }
                        placeholder="Shares sold"
                        step="any"
                        type="number"
                        value={soldLotsById.get(lot.lot_id) || ""}
                      />
                    </label>
                  ))}
                </div>
              ) : (
                <span className="field-note">
                  Select a held ticker to identify executed lots.
                </span>
              )}
            </div>
          ) : null}
          <label>
            Execution Price
            <input
              disabled={!canWrite}
              min="0"
              onChange={(event) =>
                setTradeDraft((current) => ({
                  ...current,
                  execution_price: event.target.value,
                }))
              }
              type="number"
              value={tradeDraft.execution_price}
            />
          </label>
          <label>
            Fees
            <input
              disabled={!canWrite}
              min="0"
              onChange={(event) =>
                setTradeDraft((current) => ({
                  ...current,
                  fees: event.target.value,
                }))
              }
              type="number"
              value={tradeDraft.fees}
            />
          </label>
          <label>
            Funding
            <select
              aria-label="Funding"
              disabled={!canWrite || tradeDraft.action !== "BUY"}
              name="funding_source"
              onChange={(event) =>
                setTradeDraft((current) => ({
                  ...current,
                  funding_source: event.target.value,
                }))
              }
              value={tradeDraft.funding_source || "portfolio_cash"}
            >
              <option value="portfolio_cash">Portfolio Cash</option>
              <option value="external_contribution">New Contribution</option>
            </select>
          </label>
          <label>
            Trade Date
            <input
              disabled={!canWrite}
              onChange={(event) =>
                setTradeDraft((current) => ({
                  ...current,
                  trade_date: event.target.value,
                }))
              }
              type="date"
              value={tradeDraft.trade_date}
            />
          </label>
          <button
            className="trade-execute-button tooltip-button"
            data-tooltip="Updates cash and tax lots."
            disabled={!canWrite || trading}
            onClick={executeManualTrade}
            type="button"
          >
            Execute Trade
          </button>
        </div>
      </div>
      <FidelityTradeImport
        action={tradeDraft.action}
        canWrite={canWrite}
        currentLots={availableLots}
        key={`${tradeDraft.action}-${tradeDraft.ticker}`}
        setTradeDraft={setTradeDraft}
        ticker={tradeDraft.ticker}
      />
      <div className="recommendation-execution-panel">
        <div className="panel-title-row">
          <h3>Latest Recommendation</h3>
          <p>Enter final execution prices manually after trading.</p>
        </div>
        <div className="portfolio-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th>Ticker</th>
                <th>Shares</th>
                <th>Est. Price</th>
                <th>Est. Value</th>
                <th>Est. Tax</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((action, index) => (
                <tr key={`${action.action}-${action.ticker}-${index}`}>
                  <td>
                    <button
                      className="compact-action-button recommendation-action-button"
                      disabled={
                        !canWrite
                        || ["executed", "skipped"].includes(actionStatus(action))
                      }
                      onClick={() => startRecommendedTrade(action)}
                      type="button"
                    >
                      {action.action}
                    </button>
                  </td>
                  <td>{action.ticker}</td>
                  <td>{formatNumber(action.shares)}</td>
                  <td>{formatOptionalCurrency(action.estimated_price)}</td>
                  <td>{formatCurrency(action.estimated_trade_value || 0)}</td>
                  <td>{formatCurrency(action.estimated_tax || 0)}</td>
                  <td>
                    <span className={`status-pill ${actionStatus(action)}`}>
                      {statusLabel(action)}
                    </span>
                  </td>
                </tr>
              ))}
              {!actions.length ? (
                <tr>
                  <td colSpan="7">
                    No trades are recommended for this rebalance check.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function ActivityPanel({ activity, loading, timeZone }) {
  const rows = activity?.activity || [];
  return (
    <section className="portfolio-detail-panel">
      <div className="panel-title-row">
        <h2>Activity</h2>
        <p>{loading ? "Loading" : `${formatNumber(rows.length)} records`}</p>
      </div>
      <div className="portfolio-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Summary</th>
              <th>Value</th>
              <th>Tax</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
                <tr
                  className={activityRowClassName(row, timeZone)}
                  key={row.activity_id}
                >
                <td>
                  {formatActivityDate(row.effective_date || row.recorded_at)}
                </td>
                <td>{activityLabel(row.activity_type)}</td>
                <td>{row.title}</td>
                <td>
                  {formatOptionalCurrency(
                    row.details?.trade_value ?? row.details?.portfolio_value,
                  )}
                </td>
                <td>
                  {formatOptionalCurrency(
                    row.details?.estimated_tax
                      ?? (row.activity_type === "tax_payment"
                        ? row.details?.amount
                        : null),
                  )}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan="5">No activity recorded yet.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TaxPanel({
  canWrite,
  loading,
  onPaymentChange,
  onPaymentSubmit,
  paymentDraft,
  paymentSaving,
  summary,
}) {
  const years = summary?.years || [];
  const selectedYear =
    years.find((year) => String(year.tax_year) === String(paymentDraft.tax_year))
    || years[0]
    || null;
  const rates = summary?.tax_rates || {};
  return (
    <section className="portfolio-detail-panel tax-panel">
      <div className="panel-title-row">
        <h2>Taxes</h2>
        <p>{summary?.as_of_date ? `As of ${summary.as_of_date}` : ""}</p>
      </div>
      <div className="notice tax-scope-notice">
        Portfolio-only estimate. It excludes wages, withholding, prior-year
        total tax, and activity outside this portfolio. Use the dates as
        planning reminders, not a filing calculation.
      </div>
      {loading ? <LoadingProgress label="Loading tax ledger" /> : null}
      {!loading && !years.length ? (
        <div className="notice">No tax years are available yet.</div>
      ) : null}
      {years.length ? (
        <>
          <div className="portfolio-table-wrap">
            <table className="tax-summary-table">
              <thead>
                <tr>
                  <th>Tax Year</th>
                  <th>Federal Estimate</th>
                  <th>Federal Paid</th>
                  <th>Federal Remaining</th>
                  <th>Colorado Estimate</th>
                  <th>Colorado Paid</th>
                  <th>Colorado Remaining</th>
                </tr>
              </thead>
              <tbody>
                {years.map((year) => (
                  <tr key={year.tax_year}>
                    <td>{year.tax_year}</td>
                    <td>
                      {formatTaxCurrency(year.federal?.estimated_liability)}
                    </td>
                    <td>{formatTaxCurrency(year.federal?.paid)}</td>
                    <td>{formatTaxCurrency(year.federal?.remaining)}</td>
                    <td>
                      {formatTaxCurrency(year.state?.estimated_liability)}
                    </td>
                    <td>{formatTaxCurrency(year.state?.paid)}</td>
                    <td>{formatTaxCurrency(year.state?.remaining)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="tax-rate-note">
            Federal short-term {formatPercent(rates.federal_short_term_rate || 0)},
            federal long-term {formatPercent(rates.federal_long_term_rate || 0)},
            NIIT {formatPercent(rates.niit_rate || 0)}, Colorado{" "}
            {formatPercent(rates.state_rate || 0)}.
          </p>
          {selectedYear ? (
            <div className="tax-detail-grid">
              <section>
                <div className="panel-title-row">
                  <h3>{selectedYear.tax_year} Taxable Activity</h3>
                </div>
                <dl className="tax-income-list">
                  <TaxDetail
                    label="Short-term gains"
                    value={selectedYear.income?.short_term_gain}
                  />
                  <TaxDetail
                    label="Long-term gains"
                    value={selectedYear.income?.long_term_gain}
                  />
                  <TaxDetail
                    label="Ordinary dividends"
                    value={selectedYear.income?.ordinary_income}
                  />
                  <TaxDetail
                    label="Qualified dividends"
                    value={selectedYear.income?.qualified_dividend_income}
                  />
                </dl>
              </section>
              <section>
                <div className="panel-title-row">
                  <h3>Suggested Payment Dates</h3>
                </div>
                <div className="portfolio-table-wrap">
                  <table className="tax-due-table">
                    <thead>
                      <tr>
                        <th>Payment</th>
                        <th>IRS and Colorado</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedYear.payment_dates || []).map((dueDate) => (
                        <tr key={`${dueDate.label}-${dueDate.due_date}`}>
                          <td>{dueDate.label}</td>
                          <td>{dueDate.due_date}</td>
                          <td>{tabLabel(dueDate.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          ) : null}
          <section className="tax-payment-panel">
            <div className="panel-title-row">
              <h3>Record Payment</h3>
              <p>Payments reduce portfolio cash and remain in the audit log.</p>
            </div>
            <div className="tax-payment-grid">
              <label>
                Jurisdiction
                <select
                  disabled={!canWrite || paymentSaving}
                  onChange={(event) =>
                    onPaymentChange((current) => ({
                      ...current,
                      jurisdiction: event.target.value,
                    }))
                  }
                  value={paymentDraft.jurisdiction}
                >
                  <option value="federal">IRS / Federal</option>
                  <option value="state">Colorado</option>
                </select>
              </label>
              <label>
                Tax Year
                <select
                  disabled={!canWrite || paymentSaving}
                  onChange={(event) =>
                    onPaymentChange((current) => ({
                      ...current,
                      tax_year: event.target.value,
                    }))
                  }
                  value={paymentDraft.tax_year}
                >
                  {years.map((year) => (
                    <option key={year.tax_year} value={year.tax_year}>
                      {year.tax_year}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Amount
                <input
                  disabled={!canWrite || paymentSaving}
                  inputMode="decimal"
                  min="0"
                  onChange={(event) =>
                    onPaymentChange((current) => ({
                      ...current,
                      amount: event.target.value,
                    }))
                  }
                  placeholder="0.00"
                  step="0.01"
                  type="number"
                  value={paymentDraft.amount}
                />
              </label>
              <label>
                Payment Date
                <input
                  disabled={!canWrite || paymentSaving}
                  onChange={(event) =>
                    onPaymentChange((current) => ({
                      ...current,
                      effective_date: event.target.value,
                    }))
                  }
                  type="date"
                  value={paymentDraft.effective_date}
                />
              </label>
              <label className="tax-payment-note">
                Note
                <input
                  disabled={!canWrite || paymentSaving}
                  onChange={(event) =>
                    onPaymentChange((current) => ({
                      ...current,
                      note: event.target.value,
                    }))
                  }
                  placeholder="Confirmation or memo"
                  type="text"
                  value={paymentDraft.note}
                />
              </label>
              <button
                disabled={
                  !canWrite
                  || paymentSaving
                  || !(Number(paymentDraft.amount) > 0)
                  || !paymentDraft.effective_date
                  || !paymentDraft.tax_year
                }
                onClick={onPaymentSubmit}
                type="button"
              >
                {paymentSaving ? "Recording" : "Record payment"}
              </button>
            </div>
          </section>
          {selectedYear?.payments?.length ? (
            <section>
              <div className="panel-title-row">
                <h3>{selectedYear.tax_year} Recorded Payments</h3>
              </div>
              <div className="portfolio-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Jurisdiction</th>
                      <th>Amount</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedYear.payments.map((payment, index) => (
                      <tr
                        key={`${payment.effective_date}-${payment.jurisdiction}-${index}`}
                      >
                        <td>{payment.effective_date}</td>
                        <td>
                          {payment.jurisdiction === "federal"
                            ? "IRS / Federal"
                            : "Colorado"}
                        </td>
                        <td>{formatTaxCurrency(payment.amount)}</td>
                        <td>{payment.note || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function TaxDetail({ label, value }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{formatTaxCurrency(value)}</dd>
    </>
  );
}

function LoadingProgress({ label }) {
  return (
    <div aria-live="polite" className="loading-progress" role="status">
      <div>
        <strong>{label}</strong>
        <span>
          New start dates can take about a minute while the replay is prepared.
          Controls are locked to avoid duplicate replays.
        </span>
      </div>
      <div className="loading-progress-track">
        <i />
      </div>
    </div>
  );
}

function PerformancePanel({
  canWrite,
  cashFlowDraft,
  cashFlowSaving,
  error,
  loading,
  onApplyStartDate,
  onCashFlowChange,
  onCashFlowSubmit,
  onPeriodChange,
  onStartDateChange,
  performance,
  period,
  startDate,
  startDateDraft,
}) {
  const points = performance?.points || [];
  const latest = points[points.length - 1] || {};
  const preparing = performance?.cache_status === "preparing";
  const excludedTickers = performance?.excluded_tickers || [];
  const effectiveStartDate = startDate || performance?.start_date || "";
  const effectiveStartDateDraft = startDateDraft || effectiveStartDate;
  const startDateChanged = effectiveStartDateDraft !== effectiveStartDate;
  const [chartSeriesKeys, setChartSeriesKeys] = useState(
    DEFAULT_PERFORMANCE_CHART_SERIES,
  );
  const selectedChartSeries = PERFORMANCE_CHART_SERIES.filter((series) =>
    chartSeriesKeys.includes(series.key),
  );
  const onChartSeriesToggle = (key) => {
    setChartSeriesKeys((current) => {
      if (current.includes(key)) {
        return current.length > 1
          ? current.filter((item) => item !== key)
          : current;
      }
      return [...current, key];
    });
  };
  return (
    <section className="portfolio-detail-panel">
      <div className="panel-title-row">
        <h2>Performance</h2>
        <label className="inline-select-label">
          Timeline grouping
          <select
            aria-label="Performance timeline grouping"
            disabled={loading}
            onChange={(event) => onPeriodChange(event.target.value)}
            value={period}
          >
            {PERFORMANCE_PERIODS.map((value) => (
              <option key={value} value={value}>
                {tabLabel(value)}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-select-label">
          Start
          <input
            aria-label="Performance start date"
            disabled={loading}
            onChange={(event) => onStartDateChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !loading) {
                onApplyStartDate();
              }
            }}
            type="date"
            value={effectiveStartDateDraft}
          />
        </label>
        <button
          aria-label="Apply performance start date"
          className="icon-button tooltip-button"
          data-tooltip={loading ? "Performance is loading" : "Apply start date"}
          disabled={loading || !startDateChanged}
          onClick={onApplyStartDate}
          type="button"
        >
          <RefreshCw />
        </button>
      </div>
      {loading ? <LoadingProgress label="Loading performance" /> : null}
      {error ? (
        <div className="notice performance-error">
          Performance is unavailable: {error}
        </div>
      ) : null}
      {preparing ? (
        <div className="notice">
          {performance.message || "Performance is being prepared. Try again shortly."}
        </div>
      ) : null}
      {!loading && !error && !preparing && !points.length ? (
        <div className="notice">
          No performance points recorded for this start date yet.
        </div>
      ) : null}
      {points.length ? (
        <>
          <PerformanceSummaryTable
            excludedTickers={excludedTickers}
            latest={latest}
            startDate={effectiveStartDate}
          />
          <p className="performance-note">
            Volume replay starts from the selected start value as cash and
            receives the same external contributions and withdrawals. Internal
            buys and sells stay only in the actual portfolio path. Values and
            returns are shown after estimated taxes. Costs, taxes, and trade
            counts are cumulative from the selected start date. Strategy values
            credit known market-data dividends to replay cash without automatic
            dividend reinvestment. Corporate actions are also included without
            requiring imported broker events. Market benchmarks are scaled values
            with the same external cash flows and a liquidation-tax estimate.
            Inception Hold keeps the selected-start holdings and cash as a
            static portfolio. When securities lack market history, all scenarios
            are computed on the known-market-data subset only.
          </p>
          <div className="performance-timeline-panel">
            <div className="panel-title-row">
              <h3>Performance Timeline</h3>
              <p>
                Timeline grouping changes these rows only. The summary above
                always shows the latest value through the selected window.
              </p>
            </div>
            <p className="performance-note">
              Each group uses the last available valuation snapshot in that
              group. Grouping does not synthesize days with no snapshot.
            </p>
            <PerformanceTimelineChart
              onSeriesToggle={onChartSeriesToggle}
              points={points}
              selectedSeries={selectedChartSeries}
              selectedSeriesKeys={chartSeriesKeys}
            />
            <div className="portfolio-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Date</th>
                    <th>After-Tax Value</th>
                    <th>Volume Replay</th>
                    {PERFORMANCE_BENCHMARKS.map((benchmark) => (
                      <th key={`${benchmark.key}-value`}>{benchmark.label}</th>
                    ))}
                    <th>After-Tax Return</th>
                    <th>Replay Return</th>
                    {PERFORMANCE_BENCHMARKS.map((benchmark) => (
                      <th key={`${benchmark.key}-return`}>
                        {benchmark.label} Return
                      </th>
                    ))}
                    <th>Cash</th>
                    <th>Replay Cash</th>
                    <th>Unrealized</th>
                    <th>Contributions</th>
                    <th>Recorded Dividends</th>
                    <th>Replay Dividends</th>
                    <th>Withdrawals</th>
                    <th>Actual Costs</th>
                    <th>Actual Tax</th>
                    <th>Actual Trades</th>
                    <th>Replay Costs</th>
                    <th>Replay Tax</th>
                    <th>Replay Trades</th>
                  </tr>
                </thead>
                <tbody>
                  {points.map((point) => (
                    <tr key={point.period}>
                      <td>{point.period}</td>
                      <td>{point.date}</td>
                      <td>
                        {formatCurrency(portfolioAfterTaxValue(point) || 0)}
                      </td>
                      <td>
                        {formatOptionalCurrency(
                          volumeReplayAfterTaxValue(point),
                        )}
                      </td>
                      {PERFORMANCE_BENCHMARKS.map((benchmark) => (
                        <td key={`${benchmark.key}-value`}>
                          {formatOptionalCurrency(
                            benchmarkValue(point, benchmark.key),
                          )}
                        </td>
                      ))}
                      <td>
                        {formatOptionalPercent(portfolioAfterTaxReturn(point))}
                      </td>
                      <td>
                        {formatOptionalPercent(volumeReplayAfterTaxReturn(point))}
                      </td>
                      {PERFORMANCE_BENCHMARKS.map((benchmark) => (
                        <td key={`${benchmark.key}-return`}>
                          {formatOptionalPercent(
                            benchmarkReturn(point, benchmark.key),
                          )}
                        </td>
                      ))}
                      <td>{formatCurrency(point.cash || 0)}</td>
                      <td>{formatCurrency(point.volume_replay_cash || 0)}</td>
                      <td>{formatCurrency(point.unrealized_gain || 0)}</td>
                      <td>{formatOptionalCurrency(point.contributions)}</td>
                      <td>{formatOptionalCurrency(point.dividend_income)}</td>
                      <td>
                        {formatOptionalCurrency(point.volume_replay_dividend_income)}
                      </td>
                      <td>{formatOptionalCurrency(point.withdrawals)}</td>
                      <td>{formatOptionalCurrency(point.actual_costs)}</td>
                      <td>{formatOptionalCurrency(actualTaxEstimate(point))}</td>
                      <td>{formatNumber(point.actual_trade_count || 0)}</td>
                      <td>
                        {formatOptionalCurrency(point.volume_replay_costs)}
                      </td>
                      <td>
                        {formatOptionalCurrency(
                          point.volume_replay_estimated_tax,
                        )}
                      </td>
                      <td>
                        {formatNumber(point.volume_replay_trade_count || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
      <div className="cash-flow-panel">
        <div className="panel-title-row">
          <h3>Cash Flow</h3>
          <p>
            Record outside cash entering or leaving the portfolio, or taxable
            dividend income. Recorded dividends affect the actual portfolio
            cash ledger; replay strategies credit market-data dividends to cash
            and do not automatically reinvest them.
          </p>
        </div>
        <div className="trade-entry-grid">
          <label>
            Type
            <select
              aria-label="Cash flow type"
              disabled={!canWrite}
              onChange={(event) =>
                onCashFlowChange((current) => ({
                  ...current,
                  action: event.target.value,
                }))
              }
              value={cashFlowDraft.action}
            >
              <option value="CONTRIBUTION">Contribution</option>
              <option value="WITHDRAWAL">Withdrawal</option>
              <option value="DIVIDEND">Dividend Income</option>
            </select>
          </label>
          {cashFlowDraft.action === "DIVIDEND" ? (
            <label>
              Ticker
              <input
                aria-label="Dividend ticker"
                disabled={!canWrite}
                onChange={(event) =>
                  onCashFlowChange((current) => ({
                    ...current,
                    ticker: event.target.value.toUpperCase(),
                  }))
                }
                placeholder="Optional"
                value={cashFlowDraft.ticker || ""}
              />
            </label>
          ) : null}
          <label>
            Amount
            <input
              aria-label="Cash flow amount"
              disabled={!canWrite}
              min="0"
              onChange={(event) =>
                onCashFlowChange((current) => ({
                  ...current,
                  amount: event.target.value,
                }))
              }
              type="number"
              value={cashFlowDraft.amount}
            />
          </label>
          <label>
            Date
            <input
              aria-label="Cash flow date"
              disabled={!canWrite}
              onChange={(event) =>
                onCashFlowChange((current) => ({
                  ...current,
                  effective_date: event.target.value,
                }))
              }
              type="date"
              value={cashFlowDraft.effective_date}
            />
          </label>
          <label>
            Note
            <input
              aria-label="Cash flow note"
              disabled={!canWrite}
              onChange={(event) =>
                onCashFlowChange((current) => ({
                  ...current,
                  note: event.target.value,
                }))
              }
              value={cashFlowDraft.note}
            />
          </label>
          <button
            disabled={!canWrite || cashFlowSaving}
            onClick={onCashFlowSubmit}
            type="button"
          >
            {cashFlowDraft.action === "DIVIDEND"
              ? "Record Dividend"
              : "Record Cash Flow"}
          </button>
        </div>
      </div>
    </section>
  );
}

function PerformanceTimelineChart({
  onSeriesToggle,
  points,
  selectedSeries,
  selectedSeriesKeys,
}) {
  const [tooltip, setTooltip] = useState(null);
  const chartValues = points.flatMap((point) =>
    selectedSeries.map((series) => chartSeriesValue(point, series.key)),
  ).filter((value) => value !== null);
  const chartReturns = points.flatMap((point) =>
    selectedSeries.map((series) => chartSeriesReturn(point, series.key)),
  ).filter((value) => value !== null);
  const maxValue = Math.max(1, ...chartValues) * 1.08;
  const [minReturn, maxReturn] = chartReturnDomain(chartReturns);
  const margin = { bottom: 60, left: 82, right: 74, top: 20 };
  const plotHeight = 220;
  const selectedCount = Math.max(1, selectedSeries.length);
  const groupWidth = Math.max(82, selectedCount * 25 + 44);
  const width = Math.max(
    760,
    margin.left + margin.right + points.length * groupWidth,
  );
  const height = margin.top + plotHeight + margin.bottom;
  const plotBottom = margin.top + plotHeight;
  const barGap = 5;
  const groupPadding = 12;
  const barWidth = Math.max(
    7,
    Math.min(
      18,
      (groupWidth
        - groupPadding * 2
        - Math.max(0, selectedCount - 1) * barGap)
        / selectedCount,
    ),
  );
  const valueY = (value) =>
    plotBottom - (Math.max(0, value) / maxValue) * plotHeight;
  const returnY = (value) =>
    plotBottom
    - ((value - minReturn) / (maxReturn - minReturn)) * plotHeight;
  const valueTicks = chartTicks(0, maxValue, 4);
  const returnTicks = chartTicks(minReturn, maxReturn, 4);
  const showTooltip = (event, point, series, value, returnValue) => {
    setTooltip({
      label: series.label,
      period: point.period,
      returnValue,
      value,
      x: event.clientX + 14,
      y: event.clientY + 14,
    });
  };
  const showFocusTooltip = (event, point, series, value, returnValue) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    setTooltip({
      label: series.label,
      period: point.period,
      returnValue,
      value,
      x: bounds.left + bounds.width / 2 + 14,
      y: bounds.top + 14,
    });
  };
  return (
    <div className="performance-chart-panel">
      <div
        aria-label="Performance chart strategies"
        className="performance-chart-controls"
      >
        {PERFORMANCE_CHART_SERIES.map((series) => {
          const checked = selectedSeriesKeys.includes(series.key);
          return (
            <label key={series.key}>
              <input
                checked={checked}
                disabled={checked && selectedSeriesKeys.length <= 1}
                onChange={() => onSeriesToggle(series.key)}
                type="checkbox"
              />
              <span className={`series-swatch series-${seriesClass(series.key)}`} />
              {series.label}
            </label>
          );
        })}
      </div>
      <div className="performance-chart-wrap">
        <svg
          aria-label="Performance value and return chart"
          className="performance-chart"
          role="img"
          style={{ minWidth: `${width}px` }}
          viewBox={`0 0 ${width} ${height}`}
        >
          <text className="performance-axis-title" x={margin.left} y="12">
            After-tax value
          </text>
          <text
            className="performance-axis-title"
            textAnchor="end"
            x={width - margin.right}
            y="12"
          >
            After-tax return
          </text>
          {valueTicks.map((tick) => {
            const y = valueY(tick);
            return (
              <g key={`value-${tick}`}>
                <line
                  className="performance-chart-grid"
                  x1={margin.left}
                  x2={width - margin.right}
                  y1={y}
                  y2={y}
                />
                <text
                  className="performance-axis-label"
                  textAnchor="end"
                  x={margin.left - 8}
                  y={y + 4}
                >
                  {formatAxisCurrency(tick)}
                </text>
              </g>
            );
          })}
          {returnTicks.map((tick) => {
            const y = returnY(tick);
            return (
              <g key={`return-${tick}`}>
                <line
                  className="performance-chart-return-tick"
                  x1={width - margin.right}
                  x2={width - margin.right + 5}
                  y1={y}
                  y2={y}
                />
                <text
                  className="performance-axis-label"
                  x={width - margin.right + 8}
                  y={y + 4}
                >
                  {formatAxisPercent(tick)}
                </text>
              </g>
            );
          })}
          <line
            className="performance-chart-axis"
            x1={margin.left}
            x2={margin.left}
            y1={margin.top}
            y2={plotBottom}
          />
          <line
            className="performance-chart-axis"
            x1={width - margin.right}
            x2={width - margin.right}
            y1={margin.top}
            y2={plotBottom}
          />
          <line
            className="performance-chart-axis"
            x1={margin.left}
            x2={width - margin.right}
            y1={plotBottom}
            y2={plotBottom}
          />
          {points.map((point, pointIndex) => {
            const groupX = margin.left + pointIndex * groupWidth;
            const barStart = groupX + groupPadding;
            const labelX = groupX + groupWidth / 2;
            return (
              <g key={point.period}>
                {selectedSeries.map((series, seriesIndex) => {
                  const value = chartSeriesValue(point, series.key);
                  const returnValue = chartSeriesReturn(point, series.key);
                  if (value === null) {
                    return null;
                  }
                  const x = barStart + seriesIndex * (barWidth + barGap);
                  const y = valueY(value);
                  const tooltip = chartTooltip(point, series, value, returnValue);
                  const hitWidth = Math.max(14, barWidth + 4);
                  const hitX = x - (hitWidth - barWidth) / 2;
                  return (
                    <g key={`${point.period}-${series.key}`}>
                      <rect
                        aria-label={tooltip}
                        className="performance-chart-hit"
                        height={plotHeight}
                        onBlur={() => setTooltip(null)}
                        onFocus={(event) =>
                          showFocusTooltip(
                            event,
                            point,
                            series,
                            value,
                            returnValue,
                          )
                        }
                        onPointerEnter={(event) =>
                          showTooltip(
                            event,
                            point,
                            series,
                            value,
                            returnValue,
                          )
                        }
                        onPointerLeave={() => setTooltip(null)}
                        onPointerMove={(event) =>
                          showTooltip(
                            event,
                            point,
                            series,
                            value,
                            returnValue,
                          )
                        }
                        tabIndex={0}
                        width={hitWidth}
                        x={hitX}
                        y={margin.top}
                      />
                      <rect
                        aria-hidden="true"
                        className={`performance-chart-bar series-${seriesClass(series.key)}`}
                        height={Math.max(1, plotBottom - y)}
                        width={barWidth}
                        x={x}
                        y={y}
                      />
                    </g>
                  );
                })}
                <text
                  className="performance-chart-period-label"
                  textAnchor="end"
                  transform={`rotate(-35 ${labelX} ${height - 28})`}
                  x={labelX}
                  y={height - 28}
                >
                  {point.period}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      {tooltip ? (
        <div
          className="performance-chart-tooltip"
          style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }}
        >
          <strong>{tooltip.label}</strong>
          <span>{tooltip.period}</span>
          <span>Value: {formatOptionalCurrency(tooltip.value)}</span>
          <span>Return: {formatOptionalPercent(tooltip.returnValue)}</span>
        </div>
      ) : null}
    </div>
  );
}

function SettingLabel({ info, label }) {
  const tooltipId = `setting-info-${String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <span className="setting-label-row">
      <span className="setting-label-text">{label}</span>
      <span className="setting-info-wrap">
        <button
          aria-describedby={tooltipId}
          aria-label={`${label} info`}
          className="setting-info-button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          type="button"
        >
          <Info size={13} />
        </button>
        <span
          className="setting-info-tooltip"
          id={tooltipId}
          role="tooltip"
        >
          {info}
        </span>
      </span>
    </span>
  );
}

function InfoTooltip({ label, text }) {
  return (
    <span className="inline-info-wrap">
      <button
        aria-label={label}
        className="inline-info-button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        type="button"
      >
        <Info size={12} />
      </button>
      <span className="inline-info-tooltip" role="tooltip">
        {text}
      </span>
    </span>
  );
}

function PerformanceSummaryTable({ excludedTickers, latest, startDate }) {
  const rows = [
    {
      costs: latest.actual_costs,
      description:
        "Your recorded portfolio using confirmed holdings, cash flows, trades, fees, and estimated taxes. Value is net of estimated realized and liquidation taxes.",
      key: "actual",
      label: "Actual Portfolio",
      returnValue: portfolioAfterTaxReturn(latest),
      dividendBasis: "Recorded cash events",
      tax: actualTaxEstimate(latest),
      trades: latest.actual_trade_count,
      value: portfolioAfterTaxValue(latest),
    },
    {
      costs: latest.volume_replay_costs,
      description:
        "Hypothetical production volume strategy starting from the selected start value as cash. Value is net of unpaid tax liability from the replay.",
      key: "volume",
      label: "Volume Strategy",
      returnValue: volumeReplayAfterTaxReturn(latest),
      dividendBasis: "Credited to cash",
      tax: latest.volume_replay_estimated_tax,
      trades: latest.volume_replay_trade_count,
      value: volumeReplayAfterTaxValue(latest),
    },
    ...PERFORMANCE_BENCHMARKS.map((benchmark) => ({
      benchmark: true,
      description: benchmarkDescription(benchmark.key),
      dividendBasis: benchmarkDividendBasis(benchmark.key),
      key: benchmark.key,
      label: benchmark.label,
      returnValue: benchmarkReturn(latest, benchmark.key),
      tax: benchmarkTax(latest, benchmark.key),
      value: benchmarkValue(latest, benchmark.key),
    })),
  ];
  return (
    <div className="performance-summary-wrap">
      <table className="performance-summary-table">
        <thead>
          <tr>
            <th>Scenario</th>
            <th>After-Tax Value</th>
            <th>After-Tax Return</th>
            <th>Costs</th>
            <th>Tax Estimate</th>
            <th>Trades</th>
            <th>Dividend Basis</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">
                <span className="performance-scenario-cell">
                  <span>{row.label}</span>
                  <InfoTooltip
                    label={`${row.label} info`}
                    text={row.description}
                  />
                </span>
              </th>
              <td>{formatSummaryCurrency(row.value)}</td>
              <td className={gainClassName(row.returnValue)}>
                {formatSummaryPercent(row.returnValue)}
              </td>
              <td>
                {row.benchmark ? "n/a" : formatSummaryCurrency(row.costs)}
              </td>
              <td>{formatSummaryCurrency(row.tax)}</td>
              <td>
                {row.benchmark ? "n/a" : formatSummaryCount(row.trades)}
              </td>
              <td>{row.dividendBasis}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {excludedTickers.length ? (
        <p className="performance-note">
          Historical comparisons exclude securities without market-data history:{" "}
          {excludedTickers.join(", ")}.
        </p>
      ) : null}
      <div className="performance-cash-flow-summary">
        <span>
          Start: <strong>{startDate || "No data"}</strong>
        </span>
        <span>
          Through: <strong>{latest.date || "No data"}</strong>
        </span>
        <span>
          Contributions:{" "}
          <strong>{formatSummaryCurrency(latest.contributions)}</strong>
        </span>
        <span>
          Dividends:{" "}
          <strong>{formatSummaryCurrency(latest.dividend_income)}</strong>
        </span>
        <span>
          Withdrawals:{" "}
          <strong>{formatSummaryCurrency(latest.withdrawals)}</strong>
        </span>
      </div>
    </div>
  );
}

function benchmarkDividendBasis(key) {
  if (key === "spy" || key === "inception_hold") {
    return "Credited to cash";
  }
  if (key === "nasdaq100" || key === "nasdaq_composite") {
    return "Index level";
  }
  return "Market data";
}

function benchmarkDescription(key) {
  if (key === "nasdaq100") {
    return "Nasdaq-100 index benchmark scaled to the selected start value and external cash flows.";
  }
  if (key === "nasdaq_composite") {
    return "Nasdaq Composite benchmark scaled to the selected start value and external cash flows.";
  }
  if (key === "spy") {
    return "SPY ETF benchmark scaled to the selected start value and external cash flows.";
  }
  if (key === "inception_hold") {
    return (
      "Holds the securities and cash present at the selected start date. "
      + "Later confirmed trades are ignored; external cash flows are applied "
      + "to the selected-start allocation."
    );
  }
  return "Benchmark scenario scaled to the selected start value.";
}

function chartSeriesValue(point, key) {
  if (key === "actual") {
    return portfolioAfterTaxValue(point);
  }
  if (isVolumeReplayKey(key)) {
    return volumeReplayAfterTaxValue(point);
  }
  return benchmarkValue(point, key);
}

function chartSeriesReturn(point, key) {
  if (key === "actual") {
    return portfolioAfterTaxReturn(point);
  }
  if (isVolumeReplayKey(key)) {
    return volumeReplayAfterTaxReturn(point);
  }
  return benchmarkReturn(point, key);
}

function isVolumeReplayKey(key) {
  return key === "volume";
}

function chartReturnDomain(values) {
  if (!values.length) {
    return [-0.01, 0.01];
  }
  let min = Math.min(0, ...values);
  let max = Math.max(0, ...values);
  if (Math.abs(max - min) < 0.000001) {
    min -= 0.01;
    max += 0.01;
  }
  const padding = (max - min) * 0.08;
  return [min - padding, max + padding];
}

function chartTicks(min, max, count) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || count <= 0) {
    return [];
  }
  if (Math.abs(max - min) < 0.000001) {
    return [min];
  }
  return Array.from(
    { length: count + 1 },
    (_, index) => min + ((max - min) * index) / count,
  );
}

function chartTooltip(point, series, value, returnValue) {
  return [
    series.label,
    point.period,
    `After-tax value: ${formatOptionalCurrency(value)}`,
    `After-tax return: ${formatOptionalPercent(returnValue)}`,
  ].filter(Boolean).join("\n");
}

function seriesClass(key) {
  return String(key || "unknown").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
}

function formatAxisCurrency(value) {
  const absolute = Math.abs(Number(value) || 0);
  if (absolute >= 1000000) {
    return `$${(value / 1000000).toFixed(absolute >= 10000000 ? 0 : 1)}M`;
  }
  if (absolute >= 1000) {
    return `$${(value / 1000).toFixed(absolute >= 10000 ? 0 : 1)}K`;
  }
  return formatCurrency(value);
}

function formatAxisPercent(value) {
  const absolute = Math.abs(Number(value) || 0);
  return `${(value * 100).toFixed(absolute < 0.1 ? 1 : 0)}%`;
}

function Metric({ detail = "", info = "", label, value, tone = "" }) {
  return (
    <div className={tone ? `metric-${tone}` : ""}>
      <span className="metric-label">
        {label}
        {info ? <InfoTooltip label={`${label} info`} text={info} /> : null}
      </span>
      <strong>{value}</strong>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

function exposurePolicyLabel(reason) {
  const labels = {
    adverse_market_entry: "First defensive rebalance",
    adverse_market: "Defensive",
    recovered: "Recovered",
    fully_invested: "Fully invested",
    disabled: "Disabled",
  };
  return labels[reason] || String(reason || "").replaceAll("_", " ");
}

function roleLabel(role) {
  if (role === "owner") {
    return "Owner";
  }
  if (role === "viewer") {
    return "Shared";
  }
  return role ? role[0].toUpperCase() + role.slice(1) : "Shared";
}

function recommendationSubtitle(recommendation) {
  const parts = [`As of ${recommendation.as_of_date || "unknown date"}`];
  if (recommendation.preview) {
    parts.push("hypothetical only");
  }
  if (recommendation.target?.status) {
    const age = numberOrNull(recommendation.target.age_days);
    parts.push(
      `${tabLabel(recommendation.target.status)}`
      + (age === null ? "" : ` (${formatNumber(age)}d old)`),
    );
  }
  const workflow = recommendation.workflow || {};
  if ("pending_action_count" in workflow || "action_count" in workflow) {
    const pendingActions = Number(workflow.pending_action_count || 0);
    const partialActions = Number(workflow.partial_action_count || 0);
    const skippedActions = Number(workflow.skipped_action_count || 0);
    const actionCount = Number(workflow.action_count || 0);
    const outstandingActions = pendingActions + partialActions;
    const pendingLabel = recommendationActionLabel(
      outstandingActions,
      recommendation.preview,
    );
    if (outstandingActions > 0) {
      parts.push(`${formatNumber(outstandingActions)} ${pendingLabel}`);
    } else if (skippedActions > 0) {
      parts.push(
        `rebalance completed · ${formatNumber(skippedActions)} planned trades not recorded`,
      );
    } else if (actionCount > 0) {
      parts.push("all recommended trades recorded");
    } else {
      parts.push("no trades recommended");
    }
  }
  const schedule = recommendation.rebalance_schedule || {};
  if (schedule.status === "draft") {
    parts.push("draft preview");
  }
  const generatedAt = formatDateTime(recommendation.generated_at);
  if (generatedAt) {
    parts.push(`generated ${generatedAt}`);
  }
  if (recommendation.generated_by) {
    parts.push(`by ${recommendation.generated_by}`);
  }
  return parts.join(" · ");
}

function recommendationOutstandingActionCount(recommendation) {
  const workflow = recommendation?.workflow || {};
  return (
    Number(workflow.pending_action_count || 0)
    + Number(workflow.partial_action_count || 0)
  );
}

function recommendationActionLabel(actionCount, preview) {
  const noun = actionCount === 1 ? "trade" : "trades";
  return `${noun} ${preview ? "previewed" : "pending"}`;
}

function actionStatus(action, preview = false) {
  if (preview) {
    return "preview";
  }
  const status = String(action?.status || "pending").toLowerCase();
  return ["pending", "partial", "executed", "skipped"].includes(status)
    ? status
    : "pending";
}

function statusLabel(action, preview = false) {
  return tabLabel(actionStatus(action, preview));
}

function tabLabel(value) {
  return String(value || "")
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(" ");
}

function activityLabel(value) {
  const text = String(value || "");
  if (text === "trade_buy") {
    return "Buy";
  }
  if (text === "trade_sell") {
    return "Sell";
  }
  if (text === "cash_contribution") {
    return "Contribution";
  }
  if (text === "cash_withdrawal") {
    return "Withdrawal";
  }
  if (text === "dividend_income") {
    return "Dividend";
  }
  if (text === "notification_email") {
    return "Email";
  }
  if (text === "tax_payment") {
    return "Tax Payment";
  }
  return tabLabel(text);
}

function tradeFromDraft(draft, timeZone) {
  const action = String(draft.action || "").trim().toUpperCase();
  const ticker = String(draft.ticker || "").trim().toUpperCase();
  const shares = Number(draft.shares);
  const executionPrice = Number(draft.execution_price);
  const fees = Number(draft.fees || 0);
  const tradeDate = draft.trade_date || todayDate(timeZone);
  if (
    !["BUY", "SELL"].includes(action)
    || !ticker
    || !Number.isFinite(shares)
    || shares <= 0
    || !Number.isFinite(executionPrice)
    || executionPrice <= 0
    || !tradeDate
  ) {
    return null;
  }
  const soldLots = action === "SELL"
    ? (draft.sold_lots || [])
      .map((lot) => ({
        lot_id: String(lot.lot_id || ""),
        shares: Number(lot.shares),
      }))
      .filter((lot) => lot.lot_id && Number.isFinite(lot.shares) && lot.shares > 0)
    : [];
  if (
    action === "SELL"
    && (!soldLots.length || Math.abs(sumSoldLotShares(soldLots) - shares) > 1e-6)
  ) {
    return null;
  }
  return {
    action,
    execution_price: executionPrice,
    fees: Number.isFinite(fees) && fees > 0 ? fees : 0,
    funding_source:
      action === "BUY"
        ? String(draft.funding_source || "portfolio_cash")
        : "portfolio_cash",
    shares,
    sold_lots: soldLots,
    tax_lot_method: action === "SELL" ? "specific_lots" : undefined,
    ticker,
    trade_date: tradeDate,
  };
}

function cashFlowFromDraft(draft, timeZone) {
  const action = String(draft.action || "").trim().toUpperCase();
  const amount = Number(draft.amount);
  const effectiveDate = draft.effective_date || todayDate(timeZone);
  if (
    !["CONTRIBUTION", "WITHDRAWAL", "DIVIDEND"].includes(action)
    || !Number.isFinite(amount)
    || amount <= 0
    || !effectiveDate
  ) {
    return null;
  }
  return {
    action,
    amount,
    effective_date: effectiveDate,
    note: String(draft.note || "").trim(),
    ticker: String(draft.ticker || "").trim().toUpperCase(),
  };
}

function taxPaymentFromDraft(draft, timeZone) {
  const amount = Number(draft.amount);
  const jurisdiction = String(draft.jurisdiction || "").trim().toLowerCase();
  const taxYear = Number(draft.tax_year);
  const effectiveDate = draft.effective_date || todayDate(timeZone);
  if (
    !["federal", "state"].includes(jurisdiction)
    || !Number.isInteger(taxYear)
    || taxYear < 1900
    || !Number.isFinite(amount)
    || amount <= 0
    || !effectiveDate
  ) {
    return null;
  }
  return {
    amount,
    effective_date: effectiveDate,
    jurisdiction,
    note: String(draft.note || "").trim(),
    tax_year: taxYear,
  };
}

function todayDate(timeZone = "") {
  if (!timeZone) {
    return new Date().toISOString().slice(0, 10);
  }
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "2-digit",
    timeZone,
    year: "numeric",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function dateOnly(value) {
  if (!value) {
    return "";
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(String(value))) {
    return String(value).slice(0, 10);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function createLotId() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `lot-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function SortableHeader({ currentSort, label, onSort, sortKey }) {
  const active = currentSort.key === sortKey;
  const direction = active ? currentSort.direction : "";
  return (
    <th>
      <button
        aria-label={`Sort holdings by ${label}`}
        className="table-sort-button"
        onClick={() => onSort(sortKey)}
        type="button"
      >
        {label}
        <span>{direction === "asc" ? "▲" : direction === "desc" ? "▼" : ""}</span>
      </button>
    </th>
  );
}

function NumberSetting({
  disabled,
  draft,
  info,
  label,
  name,
  setDraft,
}) {
  const value = draft.rebalance?.[name] ?? "";
  return (
    <label>
      <SettingLabel info={info} label={label} />
      <input
        disabled={disabled}
        min="0"
        onChange={(event) =>
          setDraft((current) => ({
            ...current,
            rebalance: {
              ...(current.rebalance || {}),
              [name]: event.target.value,
            },
          }))
        }
        step="0.001"
        type="number"
        value={value}
      />
    </label>
  );
}

function normalizeDraft(portfolio) {
  return {
    ...EMPTY_PORTFOLIO,
    ...portfolio,
    activation_date: portfolio.activation_date || "",
    cash: String(portfolio.cash ?? 0),
    holdings: normalizeDraftHoldings(portfolio.holdings || []),
    manual_ticker_states: normalizeManualTickerStates(
      portfolio.manual_ticker_states || [],
    ),
    notification_emails_text: (portfolio.notifications?.emails || []).join(", "),
    performance_start_date:
      portfolio.performance_start_date
      || portfolio.activation_date
      || dateOnly(portfolio.created_at)
      || "",
    status: portfolio.status || EMPTY_PORTFOLIO.status,
    notifications: {
      ...EMPTY_PORTFOLIO.notifications,
      ...(portfolio.notifications || {}),
    },
    rebalance: {
      ...EMPTY_PORTFOLIO.rebalance,
      ...(portfolio.rebalance || {}),
    },
    tax_profile: {
      ...EMPTY_PORTFOLIO.tax_profile,
      ...(portfolio.tax_profile || {}),
    },
    viewer_emails_text: (portfolio.viewer_emails || []).join(", "),
  };
}

function denormalizeDraft(draft) {
  const rebalance = { ...(draft.rebalance || {}) };
  delete rebalance.last_completed_rebalance_date;
  return {
    cash: Number(draft.cash || 0),
    holdings: denormalizeHoldings(draft.holdings || []),
    manual_ticker_states: denormalizeManualTickerStates(
      draft.manual_ticker_states || [],
    ),
    name: String(draft.name || "").trim(),
    performance_start_date: draft.performance_start_date || "",
    activation_date: draft.activation_date || "",
    status: draft.status || EMPTY_PORTFOLIO.status,
    time_zone: draft.time_zone || EMPTY_PORTFOLIO.time_zone,
    notifications: {
      ...(draft.notifications || {}),
      emails: emailsFromText(draft.notification_emails_text),
    },
    rebalance: {
      ...numbersFromMap(rebalance),
      schedule_anchor_date: rebalance.schedule_anchor_date || "",
    },
    tax_profile: numbersFromMap(draft.tax_profile),
    viewer_emails: emailsFromText(draft.viewer_emails_text),
  };
}

function portfolioFingerprint(draft) {
  return JSON.stringify({
    cash: String(draft.cash ?? ""),
    holdings: (draft.holdings || []).map((holding) => ({
      lots: (holding.lots || []).map((lot) => ({
        cost_basis_per_share: String(lot.cost_basis_per_share ?? ""),
        lot_id: lot.lot_id || "",
        purchase_date: lot.purchase_date || "",
        shares: String(lot.shares ?? ""),
      })),
      ticker: String(holding.ticker || "").trim().toUpperCase(),
    })),
    manual_ticker_states: (draft.manual_ticker_states || []).map((state) => ({
      as_of_date: state.as_of_date || "",
      note: state.note || "",
      price: String(state.price ?? ""),
      ticker: String(state.ticker || "").trim().toUpperCase(),
    })),
    name: String(draft.name || "").trim(),
    performance_start_date: String(draft.performance_start_date || ""),
    activation_date: String(draft.activation_date || ""),
    status: String(draft.status || ""),
    time_zone: String(draft.time_zone || ""),
    notification_emails_text: String(draft.notification_emails_text || ""),
    notifications: draft.notifications || {},
    rebalance: draft.rebalance || {},
    tax_profile: draft.tax_profile || {},
    viewer_emails_text: String(draft.viewer_emails_text || ""),
  });
}

function validateDraft(draft, valuedHoldings = []) {
  if (!String(draft.name || "").trim()) {
    return "Enter a portfolio name.";
  }
  if (
    draft.performance_start_date
    && !isValidDate(draft.performance_start_date)
  ) {
    return "Performance start must be a valid date.";
  }
  if (draft.activation_date && !isValidDate(draft.activation_date)) {
    return "Activation date must be a valid date.";
  }
  if (draft.status === "active" && !draft.activation_date) {
    return "Set activation date before activating the portfolio.";
  }
  if (
    draft.rebalance?.schedule_anchor_date
    && !isValidDate(draft.rebalance.schedule_anchor_date)
  ) {
    return "Rebalance anchor must be a valid date.";
  }
  const cash = Number(draft.cash || 0);
  if (!Number.isFinite(cash) || cash < 0) {
    return "Cash must be zero or greater.";
  }
  for (const holding of draft.holdings || []) {
    const ticker = String(holding.ticker || "").trim().toUpperCase();
    if (!ticker) {
      return "Every holding must have a ticker.";
    }
    const lots = holding.lots || [];
    if (!lots.length) {
      return `${ticker}: add at least one tax lot.`;
    }
    for (const [index, lot] of lots.entries()) {
      const label = `${ticker} lot ${index + 1}`;
      const sharesText = String(lot.shares ?? "").trim();
      if (!sharesText) {
        return `${label}: enter shares or delete the blank lot.`;
      }
      const shares = Number(sharesText);
      if (!Number.isFinite(shares) || shares <= 0) {
        return `${label}: shares must be greater than zero.`;
      }
      const costText = String(lot.cost_basis_per_share ?? "").trim();
      if (!costText) {
        return `${label}: enter cost/share.`;
      }
      const costBasisPerShare = Number(costText);
      if (!Number.isFinite(costBasisPerShare) || costBasisPerShare < 0) {
        return `${label}: cost/share must be zero or greater.`;
      }
      if (!isValidDate(lot.purchase_date)) {
        return `${label}: enter a valid purchase date.`;
      }
    }
  }
  const missingPrices = (valuedHoldings || [])
    .filter(
      (holding) =>
        holding.strategy_status === "unsupported"
        && !(numberOrNull(holding.price) > 0),
    )
    .map((holding) => holding.ticker);
  if (missingPrices.length) {
    return `Enter a current price before saving: ${missingPrices.join(", ")}.`;
  }
  for (const [key, value] of Object.entries(draft.tax_profile || {})) {
    const number = Number(value);
    if (
      String(value ?? "").trim() === ""
      || !Number.isFinite(number)
      || number < 0
      || number > 1
    ) {
      return `${taxRateLabel(key)} must be between 0% and 100%.`;
    }
  }
  for (const state of draft.manual_ticker_states || []) {
    const ticker = String(state.ticker || "").trim().toUpperCase();
    const priceText = String(state.price ?? "").trim();
    if (!priceText) {
      continue;
    }
    const price = Number(priceText);
    if (!ticker || !Number.isFinite(price) || price <= 0) {
      return `${ticker || "Ticker"}: manual price must be greater than zero.`;
    }
    if (!isValidDate(state.as_of_date)) {
      return `${ticker}: enter a valid manual price date.`;
    }
  }
  return "";
}

function holdingLotFromDraft(draft) {
  const ticker = String(draft.ticker || "").trim().toUpperCase();
  const shares = Number(draft.shares);
  if (!ticker || !Number.isFinite(shares) || shares <= 0) {
    return null;
  }
  return {
    lot: {
      cost_basis_per_share:
        String(draft.cost_basis_per_share ?? "").trim() === ""
          ? ""
          : Number(draft.cost_basis_per_share),
      lot_id: createLotId(),
      purchase_date: draft.purchase_date || "",
      shares,
    },
    ticker,
  };
}

function buildValuedHoldings(
  holdings,
  recommendation,
  tickerStates,
  manualTickerStates,
) {
  const valuedByTicker = new Map(
    (recommendation?.holdings || []).map((holding) => [
      String(holding.ticker || "").toUpperCase(),
      holding,
    ]),
  );
  const tickerStatesByTicker = new Map(
    Object.entries(tickerStates || {}).map(([ticker, state]) => [
      String(ticker || "").toUpperCase(),
      state,
    ]),
  );
  const manualStatesByTicker = new Map(
    (manualTickerStates || []).map((state) => [
      String(state.ticker || "").toUpperCase(),
      state,
    ]),
  );
  return (holdings || []).map((holding) => {
    const ticker = String(holding.ticker || "").toUpperCase();
    const valued = valuedByTicker.get(ticker);
    const hasTickerState = tickerStatesByTicker.has(ticker);
    const tickerState = tickerStatesByTicker.get(ticker);
    const manualState = manualStatesByTicker.get(ticker);
    if (valued) {
      return normalizeValuedHolding(valued, holding, tickerState, manualState);
    }
    if (tickerState) {
      return normalizeTickerStateHolding(holding, tickerState, manualState);
    }
    const lots = normalizeLots(holding.lots || []);
    const manualPrice = manualTickerStatePrice(manualState);
    if (manualPrice !== null) {
      return normalizeManualPriceHolding(holding, lots, manualState);
    }
    const costBasis = lots.reduce(
      (total, lot) =>
        total + Number(lot.shares || 0) * Number(lot.cost_basis_per_share || 0),
      0,
    );
    return {
      cost_basis: costBasis,
      lots,
      market_value: null,
      price: null,
      price_as_of_date: "",
      price_source: "unavailable",
      shares: Number(holding.shares || sumLotShares(lots)),
      strategy_status: hasTickerState
        ? tickerStateStrategyStatus(tickerState)
        : "price_loading",
      ticker,
      unrealized_gain: null,
      unrealized_gain_percent: null,
    };
  });
}

function normalizeTickerStateHolding(holding, tickerState, manualState) {
  const lots = normalizeLots(holding.lots || []);
  const sharesFromLots = sumLotShares(lots);
  const shares = sharesFromLots > 0 ? sharesFromLots : Number(holding.shares || 0);
  const marketPrice = tickerStatePrice(tickerState);
  const manualPrice = manualTickerStatePrice(manualState);
  const price = marketPrice ?? manualPrice;
  const costBasis = lots.reduce(
    (total, lot) =>
      total + Number(lot.shares || 0) * Number(lot.cost_basis_per_share || 0),
    0,
  );
  const marketValue = price === null ? null : shares * price;
  const unrealizedGain =
    marketValue === null ? null : marketValue - costBasis;
  return {
    cost_basis: costBasis,
    lots,
    manual_price: manualState?.price ?? "",
    manual_price_as_of_date: manualState?.as_of_date || "",
    manual_price_note: manualState?.note || "",
    market_value: marketValue,
    price,
    price_as_of_date:
      marketPrice !== null
        ? tickerState?.as_of_date || ""
        : manualState?.as_of_date || "",
    price_source:
      marketPrice !== null
        ? "market"
        : manualPrice !== null
          ? "manual"
          : "unavailable",
    shares,
    strategy_status:
      marketPrice !== null
        ? tickerStateStrategyStatus(tickerState)
        : manualPrice !== null
          ? "manual_price"
          : tickerStateStrategyStatus(tickerState),
    ticker: String(holding.ticker || tickerState?.ticker || "").toUpperCase(),
    unrealized_gain: unrealizedGain,
    unrealized_gain_percent: ratioOrNull(unrealizedGain, costBasis),
  };
}

function normalizeValuedHolding(valued, stored, tickerState, manualState) {
  const storedLots = normalizeLots(stored?.lots || []);
  const valuedLots = normalizeLots(valued.lots || []);
  const lots = storedLots.length ? storedLots : valuedLots;
  const sharesFromLots = sumLotShares(lots);
  const shares =
    (storedLots.length && sharesFromLots > 0)
      ? sharesFromLots
      : Number(stored?.shares || valued.shares || 0);
  const currentMarketPrice = tickerStatePrice(tickerState);
  const currentManualPrice = manualTickerStatePrice(manualState);
  const tickerStatus = tickerStateStrategyStatus(tickerState);
  const valuedPrice =
    valued.price_source === "manual" || tickerStatus === "unsupported"
      ? null
      : numberOrNull(valued.price);
  const price = currentMarketPrice ?? currentManualPrice ?? valuedPrice;
  let priceAsOfDate = "";
  let priceSource = "missing";
  let strategyStatus = "unsupported";
  if (currentMarketPrice !== null) {
    priceAsOfDate = tickerState?.as_of_date || "";
    priceSource = "market";
    strategyStatus = tickerStatus;
  } else if (currentManualPrice !== null) {
    priceAsOfDate = manualState?.as_of_date || "";
    priceSource = "manual";
    strategyStatus = "manual_price";
  } else if (valuedPrice !== null) {
    priceAsOfDate = valued.price_as_of_date || "";
    priceSource = valued.price_source || "market";
    strategyStatus = ["price_loading", "price_unavailable"].includes(
      tickerStatus,
    )
      ? tickerStatus
      : valued.strategy_status || "market";
  }
  const costBasis =
    lots.length
      ? lots.reduce(
          (total, lot) =>
            total
            + Number(lot.shares || 0) * Number(lot.cost_basis_per_share || 0),
          0,
        )
      : numberOrNull(valued.cost_basis);
  const marketValue =
    price === null ? numberOrNull(valued.market_value) : shares * price;
  const unrealizedGain =
    marketValue === null || costBasis === null
      ? numberOrNull(valued.unrealized_gain)
      : marketValue - costBasis;
  return {
    cost_basis: costBasis,
    lots,
    manual_price: manualState?.price ?? "",
    manual_price_as_of_date: manualState?.as_of_date || "",
    manual_price_note: manualState?.note || "",
    market_value: marketValue,
    price,
    price_as_of_date: priceAsOfDate,
    price_source: priceSource,
    shares,
    strategy_status: strategyStatus,
    ticker: String(valued.ticker || stored?.ticker || "").toUpperCase(),
    unrealized_gain: unrealizedGain,
    unrealized_gain_percent:
      ratioOrNull(unrealizedGain, costBasis)
      ?? numberOrNull(valued.unrealized_gain_percent),
  };
}

function normalizeManualPriceHolding(holding, lots, manualState) {
  const sharesFromLots = sumLotShares(lots);
  const shares = sharesFromLots > 0 ? sharesFromLots : Number(holding.shares || 0);
  const price = manualTickerStatePrice(manualState);
  const costBasis = lots.reduce(
    (total, lot) =>
      total + Number(lot.shares || 0) * Number(lot.cost_basis_per_share || 0),
    0,
  );
  const marketValue = price === null ? null : shares * price;
  const unrealizedGain =
    marketValue === null ? null : marketValue - costBasis;
  return {
    cost_basis: costBasis,
    lots,
    manual_price: manualState?.price ?? "",
    manual_price_as_of_date: manualState?.as_of_date || "",
    manual_price_note: manualState?.note || "",
    market_value: marketValue,
    price,
    price_as_of_date: manualState?.as_of_date || "",
    price_source: "manual",
    shares,
    strategy_status: "manual_price",
    ticker: String(holding.ticker || manualState?.ticker || "").toUpperCase(),
    unrealized_gain: unrealizedGain,
    unrealized_gain_percent: ratioOrNull(unrealizedGain, costBasis),
  };
}

function buildPortfolioValuation(draft, holdings, recommendation) {
  const summary = recommendation?.summary || {};
  const cash = Number(draft?.cash || summary.cash_before || 0);
  const holdingsMarketValue =
    sumKnown(holdings.map((holding) => holding.market_value))
    ?? numberOrNull(summary.holdings_market_value);
  const holdingsCostBasis =
    sumKnown(holdings.map((holding) => holding.cost_basis))
    ?? numberOrNull(summary.holdings_cost_basis);
  const unrealizedGain =
    (
      holdingsMarketValue === null || holdingsCostBasis === null
        ? null
        : holdingsMarketValue - holdingsCostBasis
    ) ?? numberOrNull(summary.unrealized_gain);
  return {
    asOfDate: recommendation?.as_of_date || "",
    cash,
    holdingsCostBasis,
    holdingsMarketValue,
    portfolioValue:
      holdingsMarketValue === null
        ? numberOrNull(summary.portfolio_value)
        : holdingsMarketValue + cash,
    unrealizedGain,
    unrealizedGainPercent:
      numberOrNull(summary.unrealized_gain_percent)
      ?? ratioOrNull(unrealizedGain, holdingsCostBasis),
    targetAgeDays: numberOrNull(recommendation?.target?.age_days),
    targetMaxAgeDays: numberOrNull(recommendation?.target?.max_age_days),
    targetStatus: recommendation?.target?.status || "",
  };
}

function targetStatusLabel(valuation) {
  const age = numberOrNull(valuation.targetAgeDays);
  if (valuation.targetStatus === "fresh") {
    return age === null ? "Fresh target" : `Fresh target · ${age}d old`;
  }
  return tabLabel(valuation.targetStatus || "unknown target");
}

function holdingStatusNote(holding, timeZone) {
  const priceAgeDays = dateAgeDays(holding.price_as_of_date, timeZone);
  const notes = [];
  if (priceAgeDays !== null && priceAgeDays > 31) {
    notes.push(
      `${holding.price_source === "manual" ? "Manual" : "Market"} price is ${priceAgeDays} days old; update it before relying on valuation or recommendations.`,
    );
  }
  if (holding.strategy_status === "manual_price") {
    notes.push("Manual price; excluded from automated sell recommendations.");
  }
  if (holding.strategy_status === "seasoning") {
    notes.push(
      "Short trading history; protected from automated sell recommendations.",
    );
  }
  if (holding.strategy_status === "unsupported") {
    notes.push("No SEP/SFP price found; enter a manual current price.");
  }
  if (holding.strategy_status === "price_loading") {
    notes.push("Checking current market-price availability.");
  }
  if (holding.strategy_status === "price_unavailable") {
    notes.push(
      "Market price is temporarily unavailable; saving remains available and valuation will update after the market-data job.",
    );
  }
  return notes.join(" ");
}

function dateAgeDays(value, timeZone) {
  const date = dateOnly(value);
  const today = todayDate(timeZone);
  if (!date || !today) {
    return null;
  }
  return Math.max(
    0,
    Math.floor(
      (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${date}T00:00:00Z`))
      / 86400000,
    ),
  );
}

function taxRateLabel(key) {
  return String(key || "Tax rate")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function recommendedSoldLots(recommendedLots, availableLots) {
  const available = new Map((availableLots || []).map((lot) => [lot.lot_id, lot]));
  return (recommendedLots || [])
    .map((lot) => ({
      lot_id: String(lot.lot_id || ""),
      shares: String(lot.shares ?? ""),
    }))
    .filter((lot) => available.has(lot.lot_id) && Number(lot.shares) > 0);
}

function sumSoldLotShares(lots) {
  return (lots || []).reduce(
    (total, lot) => total + Number(lot.shares || 0),
    0,
  );
}

function activityRowClassName(row, timeZone) {
  const classes = ["activity-row"];
  const type = String(row.activity_type || "");
  if (type === "trade_buy") {
    classes.push("activity-row-buy");
  }
  if (type === "trade_sell") {
    classes.push("activity-row-sell");
  }
  if (isTodayTradeActivity(row, timeZone)) {
    classes.push("activity-row-today");
  }
  return classes.join(" ");
}

function isTodayTradeActivity(row, timeZone) {
  const type = String(row.activity_type || "");
  if (type !== "trade_buy" && type !== "trade_sell") {
    return false;
  }
  return (
    activityDateKey(row.effective_date || row.recorded_at)
    === todayDate(timeZone)
  );
}

function activityDateKey(value) {
  if (!value) {
    return "";
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(String(value))) {
    return String(value).slice(0, 10);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function isValidDate(value) {
  const text = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return false;
  }
  const date = new Date(`${text}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === text;
}

function parseFidelityLots(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) {
    return { error: "Paste Fidelity lot rows first." };
  }
  const firstRow = splitClipboardRow(lines[0]);
  const firstRowHasHeader = findFidelityColumn(firstRow, ["acquired"]) >= 0;
  const columns = firstRowHasHeader
    ? {
        acquired: findFidelityColumn(firstRow, ["acquired"]),
        averageCostBasis: findFidelityColumn(firstRow, [
          "averagecostbasis",
          "avgcostbasis",
        ]),
        costBasisTotal: findFidelityColumn(firstRow, [
          "costbasistotal",
          "totalcostbasis",
        ]),
        quantity: findFidelityColumn(firstRow, ["quantity", "shares"]),
      }
    : {
        acquired: 0,
        averageCostBasis: 6,
        costBasisTotal: 7,
        quantity: 5,
      };
  if (columns.acquired < 0 || columns.quantity < 0) {
    return {
      error: "Paste rows with Acquired and Quantity columns.",
    };
  }
  if (columns.averageCostBasis < 0 && columns.costBasisTotal < 0) {
    return {
      error: "Paste rows with Average cost basis or Cost basis total.",
    };
  }
  const rows = lines.slice(firstRowHasHeader ? 1 : 0);
  const lots = [];
  const errors = [];
  for (const [index, row] of rows.entries()) {
    const rowErrors = [];
    const rowNumber = index + (firstRowHasHeader ? 2 : 1);
    const cells = splitClipboardRow(row);
    const purchaseDate = parseFidelityDate(cells[columns.acquired]);
    const sharesText = normalizeClipboardNumberText(cells[columns.quantity]);
    const shares = Number(sharesText);
    let costBasisPerShareText =
      columns.averageCostBasis >= 0
        ? normalizeClipboardNumberText(cells[columns.averageCostBasis])
        : "";
    const costBasisPerShare = Number(costBasisPerShareText);
    if (
      (!costBasisPerShareText || !Number.isFinite(costBasisPerShare))
      && columns.costBasisTotal >= 0
      && Number.isFinite(shares)
      && shares > 0
    ) {
      const costBasisTotal = Number(
        normalizeClipboardNumberText(cells[columns.costBasisTotal]),
      );
      if (Number.isFinite(costBasisTotal)) {
        costBasisPerShareText = trimTrailingZeros(
          (costBasisTotal / shares).toFixed(8),
        );
      }
    }
    if (!purchaseDate) {
      rowErrors.push(`row ${rowNumber}: invalid acquired date`);
    }
    if (!Number.isFinite(shares) || shares <= 0) {
      rowErrors.push(`row ${rowNumber}: invalid quantity`);
    }
    const parsedCostBasisPerShare = Number(costBasisPerShareText);
    if (
      !Number.isFinite(parsedCostBasisPerShare)
      || parsedCostBasisPerShare < 0
    ) {
      rowErrors.push(`row ${rowNumber}: invalid average cost basis`);
    }
    if (rowErrors.length) {
      errors.push(...rowErrors);
      continue;
    }
    lots.push({
      cost_basis_per_share: costBasisPerShareText,
      lot_id: createLotId(),
      purchase_date: purchaseDate,
      shares: sharesText,
    });
  }
  if (errors.length) {
    return { error: errors.slice(0, 3).join("; ") };
  }
  if (!lots.length) {
    return { error: "No valid Fidelity tax lots found." };
  }
  return { lots };
}

function splitClipboardRow(row) {
  const text = String(row || "").trim();
  if (!text) {
    return [];
  }
  const tabCells = text.split("\t").map((cell) => cell.trim());
  if (tabCells.length > 1) {
    return tabCells;
  }
  return text.split(/\s{2,}/).map((cell) => cell.trim());
}

function findFidelityColumn(headers, names) {
  const expected = new Set(names);
  return headers.findIndex((header) =>
    expected.has(normalizeFidelityHeader(header)),
  );
}

function normalizeFidelityHeader(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function parseFidelityDate(value) {
  const text = String(value || "").trim();
  if (isValidDate(text)) {
    return text;
  }
  const match = text.match(/^([A-Za-z]{3})-(\d{1,2})-(\d{4})$/);
  if (!match) {
    return "";
  }
  const month = FIDELITY_MONTHS[match[1].toLowerCase()];
  if (!month) {
    return "";
  }
  const date = `${match[3]}-${month}-${match[2].padStart(2, "0")}`;
  return isValidDate(date) ? date : "";
}

function normalizeClipboardNumberText(value) {
  const raw = String(value ?? "").trim();
  const negative = raw.startsWith("(") && raw.endsWith(")");
  const text = raw
    .replace(/[$,%+]/g, "")
    .replace(/[()]/g, "")
    .replace(/,/g, "")
    .trim();
  if (!text) {
    return "";
  }
  return negative ? `-${text}` : text;
}

function trimTrailingZeros(value) {
  return String(value).replace(/\.?0+$/, "");
}

const FIDELITY_MONTHS = {
  apr: "04",
  aug: "08",
  dec: "12",
  feb: "02",
  jan: "01",
  jul: "07",
  jun: "06",
  mar: "03",
  may: "05",
  nov: "11",
  oct: "10",
  sep: "09",
};

function sortHoldings(holdings, sort) {
  const direction = sort.direction === "desc" ? -1 : 1;
  return [...holdings].sort((left, right) => {
    if (sort.key === "ticker") {
      return direction * left.ticker.localeCompare(right.ticker);
    }
    const leftValue = numberOrNull(left[sort.key]);
    const rightValue = numberOrNull(right[sort.key]);
    if (leftValue === null && rightValue === null) {
      return left.ticker.localeCompare(right.ticker);
    }
    if (leftValue === null) {
      return 1;
    }
    if (rightValue === null) {
      return -1;
    }
    if (leftValue === rightValue) {
      return left.ticker.localeCompare(right.ticker);
    }
    return direction * (leftValue - rightValue);
  });
}

function normalizeLots(lots) {
  return (lots || []).map((lot) => ({
    cost_basis: numberOrNull(lot.cost_basis),
    cost_basis_per_share: lot.cost_basis_per_share ?? "",
    lot_id: lot.lot_id || createLotId(),
    long_term: Boolean(lot.long_term),
    market_value: numberOrNull(lot.market_value),
    price: numberOrNull(lot.price),
    purchase_date: lot.purchase_date || "",
    shares: lot.shares ?? "",
    unrealized_gain: numberOrNull(lot.unrealized_gain),
    unrealized_gain_percent: numberOrNull(lot.unrealized_gain_percent),
  }));
}

function normalizeDraftHoldings(holdings) {
  return (holdings || [])
    .map((holding) => {
      const ticker = String(holding.ticker || "").trim().toUpperCase();
      if (!ticker) {
        return null;
      }
      const lots = normalizeEditableLots(
        (holding.lots || []).length
          ? holding.lots
          : [
              {
                cost_basis_per_share: 0,
                purchase_date: "",
                shares: holding.shares || 0,
              },
            ],
      );
      const shares = sumLotShares(lots);
      if (shares <= 0) {
        return null;
      }
      return {
        ...holding,
        lots,
        shares,
        ticker,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.ticker.localeCompare(right.ticker));
}

function normalizeManualTickerStates(states) {
  return (states || [])
    .map((state) => {
      const ticker = String(state.ticker || "").trim().toUpperCase();
      if (!ticker) {
        return null;
      }
      return {
        as_of_date: state.as_of_date || "",
        note: state.note || "",
        price: state.price ?? "",
        ticker,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.ticker.localeCompare(right.ticker));
}

function normalizeEditableLots(lots) {
  return (lots || []).map((lot) => ({
    cost_basis_per_share: lot.cost_basis_per_share ?? "",
    lot_id: lot.lot_id || createLotId(),
    purchase_date: lot.purchase_date || "",
    shares: lot.shares ?? "",
  }));
}

function denormalizeManualTickerStates(states) {
  return (states || [])
    .map((state) => {
      const ticker = String(state.ticker || "").trim().toUpperCase();
      const priceText = String(state.price ?? "").trim();
      if (!ticker || !priceText) {
        return null;
      }
      const price = Number(priceText);
      if (!Number.isFinite(price) || price <= 0) {
        return null;
      }
      return {
        as_of_date: state.as_of_date || "",
        note: String(state.note || "").trim(),
        price,
        ticker,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.ticker.localeCompare(right.ticker));
}

function denormalizeHoldings(holdings) {
  return (holdings || [])
    .map((holding) => {
      const ticker = String(holding.ticker || "").trim().toUpperCase();
      const lots = (holding.lots || [])
        .map((lot) => {
          const shares = Number(lot.shares);
          if (!Number.isFinite(shares) || shares <= 0) {
            return null;
          }
          const costBasisPerShare = Number(lot.cost_basis_per_share || 0);
          return {
            cost_basis_per_share: Number.isFinite(costBasisPerShare)
              ? costBasisPerShare
              : 0,
            lot_id: lot.lot_id || createLotId(),
            purchase_date: lot.purchase_date || "",
            shares,
          };
        })
        .filter(Boolean);
      const shares = sumLotShares(lots);
      if (!ticker || shares <= 0) {
        return null;
      }
      return {
        lots,
        shares,
        ticker,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.ticker.localeCompare(right.ticker));
}

function newEditableLot() {
  return {
    cost_basis_per_share: "",
    lot_id: createLotId(),
    purchase_date: "",
    shares: "",
  };
}

function lotCost(shares, costBasisPerShare) {
  if (String(shares ?? "").trim() === "") {
    return null;
  }
  if (String(costBasisPerShare ?? "").trim() === "") {
    return null;
  }
  const sharesNumber = Number(shares);
  const costNumber = Number(costBasisPerShare);
  if (!Number.isFinite(sharesNumber) || !Number.isFinite(costNumber)) {
    return null;
  }
  return sharesNumber * costNumber;
}

function tickerStatePrice(tickerState) {
  return numberOrNull(tickerState?.state?.price ?? tickerState?.price);
}

function manualTickerStatePrice(manualState) {
  return numberOrNull(manualState?.price);
}

function tickerStateStrategyStatus(tickerState) {
  if (!tickerState) {
    return "price_loading";
  }
  if (tickerState?.availability === "unsupported") {
    return "unsupported";
  }
  if (tickerState?.availability === "unavailable") {
    return "price_unavailable";
  }
  if (tickerState?.availability === "loading") {
    return "price_loading";
  }
  const tradingDays = numberOrNull(tickerState?.state?.trading_days);
  if (tradingDays !== null && tradingDays < 252) {
    return "seasoning";
  }
  return "market";
}

function sumLotShares(lots) {
  return (lots || []).reduce(
    (total, lot) => total + Number(lot.shares || 0),
    0,
  );
}

function sumKnown(values) {
  const known = values.filter((value) => value !== null);
  if (!known.length) {
    return null;
  }
  return known.reduce((total, value) => total + value, 0);
}

function ratioOrNull(numerator, denominator) {
  if (numerator === null || denominator === null || denominator <= 0) {
    return null;
  }
  return numerator / denominator;
}

function numberOrNull(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatCompactDate(value) {
  if (!value || !isValidDate(value)) {
    return "";
  }
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T00:00:00Z`));
}

function portfolioAfterTaxValue(point) {
  return (
    numberOrNull(point?.portfolio_after_tax_value)
    ?? numberOrNull(point?.portfolio_value)
  );
}

function portfolioAfterTaxReturn(point) {
  return (
    numberOrNull(point?.return_after_tax_since_start)
    ?? numberOrNull(point?.return_since_start)
  );
}

function actualTaxEstimate(point) {
  return (
    numberOrNull(point?.actual_tax_liability)
    ?? numberOrNull(point?.actual_estimated_tax)
  );
}

function volumeReplayAfterTaxValue(point) {
  return (
    numberOrNull(point?.volume_replay_after_tax_value)
    ?? numberOrNull(point?.volume_replay_value)
  );
}

function volumeReplayAfterTaxReturn(point) {
  return (
    numberOrNull(point?.volume_replay_after_tax_return_since_start)
    ?? numberOrNull(point?.volume_replay_return_since_start)
  );
}

function benchmarkValue(point, key) {
  const benchmark = point?.benchmarks?.[key];
  return (
    numberOrNull(benchmark?.after_tax_value)
    ?? numberOrNull(benchmark?.value)
  );
}

function benchmarkReturn(point, key) {
  const benchmark = point?.benchmarks?.[key];
  return (
    numberOrNull(benchmark?.after_tax_return_since_start)
    ?? numberOrNull(benchmark?.return_since_start)
  );
}

function benchmarkTax(point, key) {
  return numberOrNull(point?.benchmarks?.[key]?.tax_estimate);
}

function formatOptionalCurrency(value) {
  return value === null || value === undefined ? "—" : formatCurrency(value);
}

function formatTaxCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: "currency",
  }).format(numberOrNull(value) || 0);
}

function formatOptionalPercent(value) {
  return value === null || value === undefined ? "—" : formatPercent(value);
}

function formatSummaryCurrency(value) {
  return numberOrNull(value) === null ? "No data" : formatCurrency(value);
}

function formatSummaryPercent(value) {
  return numberOrNull(value) === null ? "No data" : formatPercent(value);
}

function formatSummaryCount(value) {
  return numberOrNull(value) === null ? "No data" : formatNumber(value);
}

function formatDateTime(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function formatActivityDate(value) {
  if (!value) {
    return "";
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    return value;
  }
  return formatDateTime(value) || value;
}

function gainTone(value) {
  const number = numberOrNull(value);
  if (number === null || number === 0) {
    return "";
  }
  return number > 0 ? "positive" : "negative";
}

function gainClassName(value) {
  const tone = gainTone(value);
  return tone ? `gain-${tone}` : "";
}

function emailsFromText(text) {
  return String(text || "")
    .split(",")
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);
}

function numbersFromMap(values) {
  return Object.fromEntries(
    Object.entries(values || {}).map(([key, value]) => {
      const number = Number(value);
      return [key, Number.isFinite(number) ? number : value];
    }),
  );
}
